import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TenderHelperService } from 'src/app/shared/services/tender-helper.service';
import { UserService } from 'src/app/shared/services/user.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { EvaluationCriteriaData } from 'src/app/shared/components/evaluation-criteria/evaluation-criteria.component';
import { TableColumn } from 'src/app/shared/models/app.models';

@Component({
  selector: 'app-practice-details',
  templateUrl: '../../../shared/templates/tender-practice-details.template.html',
  styleUrls: ['./practice-details.component.scss']
})
export class PracticeDetailsComponent implements OnInit {
  AppConstants = AppConstants;
  isPractice = true; // Flag to identify this as practice component
  tenderId: string = '';
  tenderDetails: any = null;
  tenderForm!: FormGroup;
  activeTab = 'Practice';
  tabs = ['Practice', 'Documents', 'Workflow'];

  // Dropdown options
  tenderTypes: string[] = ['Public', 'Private'];
  rfpRfqOptions: string[] = ['RFP', 'RFQ'];
  tenderClassifications: string[] = ['Category A', 'Category B', 'Category C'];
  currencies: string[] = [];
  currenciesFromAPI: any[] = [];
  euDepartments: string[] = [];
  euDepartmentsFromAPI: any[] = [];
  spocOptions: string[] = ['SPOC 1', 'SPOC 2', 'SPOC 3'];
  purchaseRequisitions: string[] = [];
  purchaseRequisitionsFromAPI: any[] = [];

  // Tender Line dropdown options
  vendorAccounts: string[] = [];
  vendorNames: { [key: string]: string } = {};
  vendorDisplayToId: { [key: string]: string } = {};
  vendorsFromAPI: any[] = [];
  evaluationTypes: string[] = ['Technical', 'Commercial'];

  // Evaluation Criteria - using shared component (same as tenders now)
  evaluationCriteriaData?: EvaluationCriteriaData;
  private _latestEvaluationData?: EvaluationCriteriaData;

  // Received biddings data
  receivedBiddings: any[] = [];
  selectedBiddingId: string | null = null;

  // Extend closure date modal
  showExtendClosureDateModal: boolean = false;
  extendedClosureDate: string = '';
  minDate: string = '';

  // Allow to participate modal
  showAllowToParticipateModal: boolean = false;

  // Withdraw modal
  showWithdrawModal: boolean = false;

  // Decline modal
  showDeclineModal: boolean = false;
  declineRegretLetterFile: File | null = null;

  // Award modal
  showAwardModal: boolean = false;
  awardLetterFile: File | null = null;
  awardRegretLetterFile: File | null = null;

  // Bidding documents modal
  showBiddingDocumentsModal: boolean = false;
  biddingDocuments: any = {
    commercialProposal: [],
    technicalProposal: [],
    tenderBond: [],
    performanceBond: [],
    otherDocuments: []
  };
  isLoadingBiddingDocuments: boolean = false;

  // Table columns for bids
  bidsTableColumns: TableColumn[] = [];

  initializeBidsTableColumns(): void {
    this.bidsTableColumns = [
      {
        field: 'biddingNumber',
        header: '',
        type: 'radio',
        radioGroupName: 'selectedBidding',
        isSelected: (row: any) => this.selectedBiddingId === row.biddingNumber,
        onSelect: (row: any, event: Event) => {
          event.stopPropagation();
          this.selectBidding(row.biddingNumber);
        }
      },
      {
        field: 'biddingNumber',
        header: 'Bidding number',
        type: 'text',
        filterable: true
      },
      {
        field: 'vendorAccount',
        header: 'Vendor account',
        type: 'link',
        filterable: true
      },
      {
        field: 'vendorName',
        header: 'Vendor name',
        type: 'text',
        filterable: true
      },
      {
        field: 'quoteValidUntil',
        header: 'Quote Valid until',
        type: 'text',
        filterable: true
      },
      {
        field: 'closureDate',
        header: 'Closure Date',
        type: 'text',
        filterable: true
      },
      {
        field: 'awardingValue',
        header: this.isPractice ? 'Quote Value' : 'Awarding value',
        type: 'number',
        format: '1.2-2',
        filterable: true
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeColor: (row: any) => this.getStatusColor(row.status) as any,
        badgeSize: 'small',
        filterable: true
      },
      {
        field: 'document',
        header: 'Document',
        type: 'custom',
        customTemplate: (row: any) => {
          if (row.status === 'Regret') {
            return `<a href="javascript:void(0)" class="text-primary text-decoration-underline cursor-pointer" data-action="download-regret" data-bid-id="${row.biddingNumber}" style="cursor: pointer;">
              <i class="fas fa-download me-1"></i>View Regret Letter
            </a>`;
          } else if (row.status === 'Awarded') {
            return `<a href="javascript:void(0)" class="text-primary text-decoration-underline cursor-pointer" data-action="download-award" data-bid-id="${row.biddingNumber}" style="cursor: pointer;">
              <i class="fas fa-download me-1"></i>View Award Letter
            </a>`;
          }
          return '<span class="text-muted">-</span>';
        }
      }
    ];
  }

  // Workflow data
  tenderStatus: string = 'In Progress';
  workflowStatus: string = '';
  statusDescription: string = 'Your tender is currently under evaluation. The evaluation committee is reviewing all submitted bids.';
  expectedCompletionDate: string = '25/09/2025';
  timelineSteps: any[] = [];
  workflowAction: 'Submit' | 'Approve' | 'Publish' = 'Submit';
  showConfirmDialog: boolean = false;
  pendingAction: 'Submit' | 'Approve' | 'Publish' | 'Request Change' | null = null;
  workflowComments: string = '';
  isEditMode: boolean = false;
  private requestedEditMode: boolean = false;
  originalFormData: any = null;

  // Workflow user tracking
  createdBy: string = '';
  createdByDate: string = '';
  submittedBy: string = '';
  submittedByDate: string = '';
  approvedBy: string = '';
  approvedByDate: string = '';
  publishedBy: string = '';
  publishedByDate: string = '';

  // Workflow assignments (who is responsible for each step)
  assignedToSubmit: string = '';
  assignedToApprove: string = '';
  assignedToPublish: string = '';
  workflowStages: any[] = [];

  // Workflow history
  workflowHistory: any[] = [];

  // Documents data
  tenderDocuments: any[] = [];
  deletedTenderDocumentIds: string[] = [];
  bidDocuments: any[] = [];
  groupedAttachments: { attachmentFor: string; items: any[] }[] = [];
  isLoadingDocuments: boolean = false;

  // Approval email restriction
  readonly APPROVAL_EMAIL = 'msaaim@newtecx.com';

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private _http: HttpClient,
    private _fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService,
    private tenderHelper: TenderHelperService,
    private userService: UserService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.initializeBidsTableColumns();
    // Load dropdown data first
    this.loadCurrencies();
    this.loadPurchaseRequisitions();
    this.loadVendors();
    this.loadDepartments();

    // Check if edit mode is requested via query parameter
    this._route.queryParams.subscribe(queryParams => {
      this.requestedEditMode = queryParams['edit'] === 'true';
    });

    this._route.params.subscribe(params => {
      this.tenderId = params['id'];
      if (this.tenderId) {
        this.fetchTenderDetails().then(() => {
          // If edit mode was requested, enable it after data is loaded
          if (this.requestedEditMode) {
            this.editTender();
          }
        });
      }
    });
  }

  initializeForm(): void {
    this.tenderForm = this._fb.group({
      tenderNumber: [''],
      version: ['1'],
      tenderName: [''],
      tenderType: [''],
      rfpRfq: [''],
      tenderClassification: [''],
      company: [''],
      euDepartment: [''],
      euDepartmentSpoc: [''],
      purchaseRequisition: [''],
      managedBySpoc: [''],
      managedByContactDetails: [''],
      currency: [''],
      tenderValue: [''],
      tenderValueInQAR: [''],
      awaitingValue: [''],
      awardingValueInQAR: [''],
      startDateOfTender: [''],
      acceptanceDeadline: [''],
      extendedAcceptanceDeadline: [''],
      note: [''],
      recommendationJustification: [''],
      isJoiningFeeRequired: ['No'],
      joiningFee: [''],
      bidClosureDate: [''],
      // Tender Line FormControls
      backgroundAndSOW: [''],
      technicalEvaluation: [''],
      commercialEvaluation: [''],
      // Pre-Qualification Criteria
      tenderBondRequired: ['No'],
      tenderBondDetails: [''],
      tenderBondValidityDate: [''],
      performanceBondRequired: ['No'],
      performanceBondDetails: [''],
      performanceBondValidityDate: [''],
      // Tender Line FormArrays
      selectedVendors: this._fb.array([])
    });
    // Disable all controls for read-only mode
    this.tenderForm.disable();

    // Initialize evaluation criteria data for shared component
    this.evaluationCriteriaData = {
      evaluationType: 'Separate',
      technicalWeightage: 100.00,
      commercialWeightage: 100.00,
      technicalEvaluationCriteria: [],
      commercialEvaluationCriteria: []
    };
  }

  get canEditTender(): boolean {
    const status = this.getTrimmedValue(this.tenderForm.getRawValue()?.status || this.tenderStatus);
    return status.toLowerCase() === 'draft';
  }

  get canCloseBidding(): boolean {
    const status = this.getTrimmedValue(this.tenderForm.getRawValue()?.status || this.tenderStatus);
    return !this.isEditMode && status.toLowerCase() === 'open';
  }

  get canReopenBidding(): boolean {
    const status = this.getTrimmedValue(this.tenderForm.getRawValue()?.status || this.tenderStatus);
    return !this.isEditMode && status.toLowerCase() === 'closedforbidding';
  }

  get isPrivateTender(): boolean {
    const tenderType = this.getTrimmedValue(this.tenderForm.getRawValue()?.tenderType || this.tenderForm.get('tenderType')?.value);
    return tenderType.toLowerCase() === 'private';
  }
  get selectedVendorsFormArray(): FormArray {
    return this.tenderForm.get('selectedVendors') as FormArray;
  }

  async fetchTenderDetails(): Promise<void> {
    this._loaderService.showLoader();

    try {
      // Call the API to get tender details
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.GetTenderByTenderID}`;
      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url, { tenderNumber: this.tenderId })
      );

      // Check if request was successful
      if (!response?.isSuccess) {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || 'Failed to fetch tender details';
        console.error(`[Tender Details] API Error: ${errorMsg}`);
        this._loaderService.hideLoader();
        return;
      }

      // Extract tender data from response
      const tenderData = response?.data || {};

      if (!tenderData || Object.keys(tenderData).length === 0) {
        console.error('[Tender Details] No tender data found in response');
        this._loaderService.hideLoader();
        return;
      }

      // Map API response to form structure
      const formData = {
        tenderNumber: this.getTrimmedValue(tenderData.tenderNumber),
        version: this.getTrimmedValue(tenderData.version) || '1',
        tenderName: this.getTrimmedValue(tenderData.tenderName),
        status: this.getTrimmedValue(tenderData.status),
        tenderType: this.getTrimmedValue(tenderData.tenderType),
        rfpRfq: this.getTrimmedValue(tenderData.rfpRfq),
        tenderClassification: this.getTrimmedValue(tenderData.tenderClassification),
        company: this.getTrimmedValue(tenderData.company),
        euDepartment: this.getDepartmentDisplayName(tenderData.euDepartment),
        euDepartmentSpoc: this.getTrimmedValue(tenderData.euDepartmentSpoc),
        purchaseRequisition: this.getTrimmedValue(tenderData.purchaseRequisition),
        managedBySpoc: this.getTrimmedValue(tenderData.managedBySpoc),
        managedByContactDetails: this.getTrimmedValue(tenderData.managedByContactDetails),
        currency: this.getTrimmedValue(tenderData.currency),
        tenderValue: tenderData.tenderValue?.toString() || '',
        tenderValueInQAR: tenderData.tenderValueInQAR?.toString() || '',
        awaitingValue: tenderData.awaitingValue?.toString() || '',
        awardingValueInQAR: tenderData.awardingValueInQAR?.toString() || '',
        startDateOfTender: this.formatDate(tenderData.startDateOfTender),
        acceptanceDeadline: this.formatDate(tenderData.acceptanceDeadline),
        extendedAcceptanceDeadline: this.formatDate(tenderData.extendedAcceptanceDeadline),
        note: this.getTrimmedValue(tenderData.note),
        recommendationJustification: this.getTrimmedValue(tenderData.recommendationJustification),
        isJoiningFeeRequired: this.convertBooleanToYesNo(tenderData.isJoiningFeeRequired),
        joiningFee: tenderData.joiningFee?.toString() || '',
        bidClosureDate: this.formatDate(tenderData.bidClosureDate),
        backgroundAndSOW: this.getTrimmedValue(tenderData.backgroundAndSOW),
        technicalEvaluation: this.getTrimmedValue(tenderData.technicalEvaluation),
        commercialEvaluation: this.getTrimmedValue(tenderData.commercialEvaluation),
        tenderBondRequired: this.convertBooleanToYesNo(tenderData.tenderBondRequired),
        tenderBondDetails: this.getTrimmedValue(tenderData.tenderBondDetails),
        tenderBondValidityDate: this.formatDate(tenderData.tenderBondValidityDate),
        performanceBondRequired: this.convertBooleanToYesNo(tenderData.performanceBondRequired),
        performanceBondDetails: this.getTrimmedValue(tenderData.performanceBondDetails),
        performanceBondValidityDate: this.formatDate(tenderData.performanceBondValidityDate)
      };

      // Populate form with API data
      this.tenderForm.patchValue(formData);

      // Disable dropdown fields in view mode
      if (!this.isEditMode) {
        this.disableDropdownFields();
      }

      // Populate selected vendors FormArray
      this.populateSelectedVendors(tenderData.selectedVendors || tenderData.SelectedVendors || []);

      // Populate evaluation criteria (using shared component now)
      this.populateEvaluationCriteria(tenderData);

      // Trigger change detection after all data is loaded
      this.cdr.detectChanges();

      // Set tender details for header
      this.tenderDetails = {
        tenderId: this.tenderId || formData.tenderNumber,
        description: formData.backgroundAndSOW || formData.tenderName || '',
        startDate: tenderData.startDateOfTender ? new Date(tenderData.startDateOfTender) : null,
        closureDate: tenderData.acceptanceDeadline ? new Date(tenderData.acceptanceDeadline) : null,
        managedBy: formData.managedBySpoc || '',
        bankAccount: formData.company || '',
        email: this.extractEmail(formData.managedByContactDetails),
        contactNumber: this.extractPhone(formData.managedByContactDetails),
        budget: tenderData.tenderValue || 0,
        biddersCount: (tenderData.selectedVendors || tenderData.SelectedVendors || []).length,
        type: formData.tenderType || '',
        bidValidUntil: tenderData.bidClosureDate ? new Date(tenderData.bidClosureDate) : null
      };

      // Update tenderStatus from actual data if available
      if (formData.status) {
        this.tenderStatus = formData.status;
      }

      // Extract workflow user data from tenderData
      // Check both camelCase and PascalCase properties
      this.createdBy = tenderData.createdBy || tenderData.CreatedBy || '';
      this.createdByDate = tenderData.createdAt ? this.formatDateTime(tenderData.createdAt) : (tenderData.CreatedAt ? this.formatDateTime(tenderData.CreatedAt) : '');

      // Extract workflow user info - check for specific workflow fields or use ModifiedBy based on workflow status
      // Check for workflow status in multiple possible property names (camelCase, PascalCase, etc.)
      const workflowStatusValue = tenderData.workFlowStatus || tenderData.WorkFlowStatus ||
                                   tenderData.workflowStatus || tenderData.WorkflowStatus || '';

      // Store workflow status for display
      this.workflowStatus = this.formatWorkflowStatus(workflowStatusValue);

      const modifiedBy = tenderData.modifiedBy || tenderData.ModifiedBy || '';
      const modifiedAt = tenderData.modifiedAt || tenderData.ModifiedAt;

      // If workflow status is "Not Submitted" or empty, reset all workflow fields
      // This ensures the timeline shows correctly when status is reset
      const workflowStatusLower = (workflowStatusValue || '').toString().toLowerCase().trim();
      if (workflowStatusLower === 'not submitted' || workflowStatusLower === 'notsubmitted' ||
          workflowStatusLower === '0' || workflowStatusLower === '' ||
          (!workflowStatusValue && !tenderData.status)) {
        // Reset all workflow completion fields - tender is back to initial state
        this.submittedBy = '';
        this.submittedByDate = '';
        this.approvedBy = '';
        this.approvedByDate = '';
        this.publishedBy = '';
        this.publishedByDate = '';
      }
      // If workflow status is PendingApproval, ModifiedBy contains who submitted it
      else if (workflowStatusValue === 'PendingApproval' || workflowStatusValue === 1) {
        this.submittedBy = modifiedBy || '';
        this.submittedByDate = modifiedAt ? this.formatDateTime(modifiedAt) : '';
        // Reset later steps
        this.approvedBy = '';
        this.approvedByDate = '';
        this.publishedBy = '';
        this.publishedByDate = '';
      }
      // If workflow status is Approved, ModifiedBy contains who approved it
      else if (workflowStatusValue === 'Approved' || workflowStatusValue === 2) {
        this.approvedBy = modifiedBy || '';
        this.approvedByDate = modifiedAt ? this.formatDateTime(modifiedAt) : '';
        // Also check if there's a previous ModifiedBy for submittedBy
        this.submittedBy = tenderData.submittedBy || tenderData.SubmittedBy || '';
        this.submittedByDate = tenderData.submittedAt ? this.formatDateTime(tenderData.submittedAt) : (tenderData.SubmittedAt ? this.formatDateTime(tenderData.SubmittedAt) : '');
        // Reset publishing step
        this.publishedBy = '';
        this.publishedByDate = '';
      }
      // If status is Open (published), ModifiedBy contains who published it
      else if ((tenderData.status === 'Open' || tenderData.Status === 'Open') && (this.workflowStatus || '').toLowerCase().trim() === 'published') {
        this.publishedBy = modifiedBy || '';
        this.publishedByDate = modifiedAt ? this.formatDateTime(modifiedAt) : '';
        // Get previous workflow info
        this.submittedBy = tenderData.submittedBy || tenderData.SubmittedBy || '';
        this.submittedByDate = tenderData.submittedAt ? this.formatDateTime(tenderData.submittedAt) : (tenderData.SubmittedAt ? this.formatDateTime(tenderData.SubmittedAt) : '');
        this.approvedBy = tenderData.approvedBy || tenderData.ApprovedBy || '';
        this.approvedByDate = tenderData.approvedAt ? this.formatDateTime(tenderData.approvedAt) : (tenderData.ApprovedAt ? this.formatDateTime(tenderData.ApprovedAt) : '');
      }

      // Extract workflow assignments
      this.assignedToSubmit = tenderData.assignedToSubmit || tenderData.AssignedToSubmit || '';
      this.assignedToApprove = tenderData.assignedToApprove || tenderData.AssignedToApprove || '';
      this.assignedToPublish = tenderData.assignedToPublish || tenderData.AssignedToPublish || '';

      const workflowStageData = tenderData.workflowStages || tenderData.WorkflowStages || [];
      this.workflowStages = Array.isArray(workflowStageData) ? workflowStageData.map((stage: any) => ({
        stageNumber: Number(stage.stageNumber ?? stage.StageNumber ?? 0),
        stepName: stage.stepName || stage.StepName || '',
        responsibleRole: stage.responsibleRole || stage.ResponsibleRole || stage.userRole || stage.UserRole || '',
        description: stage.description || stage.Description || ''
      })).filter((stage: any) => stage.stepName) : [];

      // Debug: Log workflow assignment data from API
      console.log('[Tender Details] Workflow assignments from API:', {
        assignedToSubmit: this.assignedToSubmit,
        assignedToApprove: this.assignedToApprove,
        assignedToPublish: this.assignedToPublish,
        rawData: {
          assignedToSubmit: tenderData.assignedToSubmit,
          AssignedToSubmit: tenderData.AssignedToSubmit,
          assignedToApprove: tenderData.assignedToApprove,
          AssignedToApprove: tenderData.AssignedToApprove,
          assignedToPublish: tenderData.assignedToPublish,
          AssignedToPublish: tenderData.AssignedToPublish
        }
      });

      // Extract workflow history
      const historyData = tenderData.workflowHistory || tenderData.WorkflowHistory || [];
      this.workflowHistory = Array.isArray(historyData) ? historyData.map((h: any) => ({
        id: h.id || h.ID,
        action: h.action || h.Action,
        fromStatus: h.fromStatus || h.FromStatus,
        toStatus: h.toStatus || h.ToStatus,
        comments: h.comments || h.Comments,
        actionBy: h.actionBy || h.ActionBy,
        actionDate: h.actionDate || h.ActionDate
      })) : [];

      // Populate workflow fields from history if not already set
      // This ensures we have complete workflow information even if main data is incomplete
      // BUT: Skip history population if workflow status is "Not Submitted" (we already reset fields above)
      const currentWorkflowStatusLower = (this.workflowStatus || '').toLowerCase().trim();
      const isNotSubmitted = currentWorkflowStatusLower === 'not submitted' || currentWorkflowStatusLower === 'notsubmitted' || !this.workflowStatus;
      const isPublishedWorkflow = currentWorkflowStatusLower === 'published';

      if (!isPublishedWorkflow) {
        this.publishedBy = '';
        this.publishedByDate = '';
      }

      if (!isNotSubmitted && this.workflowHistory && this.workflowHistory.length > 0) {
        // Sort history by date to process in chronological order
        const sortedHistory = [...this.workflowHistory].sort((a: any, b: any) => {
          const dateA = a.actionDate ? new Date(a.actionDate).getTime() : 0;
          const dateB = b.actionDate ? new Date(b.actionDate).getTime() : 0;
          return dateA - dateB;
        });

        // Find Submit action (look for transition to PendingApproval)
        if (!this.submittedBy) {
          const submitAction = sortedHistory.find((h: any) =>
            (h.action || '').toLowerCase() === 'submit' ||
            (h.toStatus || '').toLowerCase() === 'pendingapproval' ||
            (h.toStatus || '').toLowerCase() === 'pending approval' ||
            (h.toStatus || '').toLowerCase() === 'pendingapproval'
          );
          if (submitAction && submitAction.actionBy) {
            this.submittedBy = submitAction.actionBy;
            this.submittedByDate = submitAction.actionDate ? this.formatDateTime(submitAction.actionDate) : '';
          }
        }

        // Find Approve action (look for transition to Approved)
        if (!this.approvedBy) {
          const approveAction = sortedHistory.find((h: any) =>
            (h.action || '').toLowerCase() === 'approve' ||
            (h.toStatus || '').toLowerCase() === 'approved'
          );
          if (approveAction && approveAction.actionBy) {
            this.approvedBy = approveAction.actionBy;
            this.approvedByDate = approveAction.actionDate ? this.formatDateTime(approveAction.actionDate) : '';
          }
        }

        // Find Publish action (look for transition to Open/Published)
        if (isPublishedWorkflow && !this.publishedBy) {
          const publishAction = sortedHistory.find((h: any) =>
            (h.action || '').toLowerCase() === 'publish' ||
            (h.toStatus || '').toLowerCase() === 'open' ||
            (h.toStatus || '').toLowerCase() === 'published'
          );
          if (publishAction && publishAction.actionBy) {
            this.publishedBy = publishAction.actionBy;
            this.publishedByDate = publishAction.actionDate ? this.formatDateTime(publishAction.actionDate) : '';
          }
        }

        // If approved but submittedBy is still empty, ensure submission is marked as completed
        // This handles cases where the API doesn't return submittedBy but we know it was submitted
        if (this.approvedBy && !this.submittedBy) {
          // Find the first action that transitions from a non-pending state to pending approval
          const firstSubmitAction = sortedHistory.find((h: any) =>
            (h.toStatus || '').toLowerCase().includes('pending') &&
            !(h.fromStatus || '').toLowerCase().includes('pending')
          );
          if (firstSubmitAction && firstSubmitAction.actionBy) {
            this.submittedBy = firstSubmitAction.actionBy;
            this.submittedByDate = firstSubmitAction.actionDate ? this.formatDateTime(firstSubmitAction.actionDate) : '';
          }
        }
      }

      // Initialize Workflow data
      this.initializeWorkflowData();

      // Fetch received biddings for this tender
      await this.fetchReceivedBiddings();

      // Trigger change detection after workflow is initialized
      this.cdr.detectChanges();

    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      console.error(`[Tender Details] Exception while fetching tender details:`, errorMessage, error);
    } finally {
      this._loaderService.hideLoader();
    }

    return Promise.resolve();
  }

  async fetchReceivedBiddings(): Promise<void> {
    if (!this.tenderId) {
      console.warn('[Tender Details] No tender ID available to fetch bids');
      return;
    }

    try {
      // Call the API to get bids by tender number
      // The endpoint uses path parameter: [HttpGet("[Action]/{tenderNumber}")]
      this.apiService.setBaseUrl(environment.API_URL);
      // Construct URL with tender number as path parameter
      const url = `${environment.API_URL}/api/Bids/${AppConstants.ACTIONS.GetBidsByTenderNumber}/${encodeURIComponent(this.tenderId)}`;
      console.log('[Tender Details] Fetching bids for tender:', this.tenderId);
      console.log('[Tender Details] Full URL:', url);

      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url)
      );

      // Check if request was successful
      if (!response?.isSuccess) {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || 'Failed to fetch bids';
        console.error(`[Tender Details] API Error fetching bids: ${errorMsg}`);
        this.receivedBiddings = [];
        return;
      }

      // Extract bids data from response
      const bidsData = response?.data || [];

      // Map API response to component format
      this.receivedBiddings = bidsData.map((bid: any) => ({
        biddingNumber: bid.bidId || '',
        vendorId: bid.vendorId || '',
        vendorAccount: bid.vendorId || '',
        vendorName: bid.vendorName || '',
        performancebondrequired: bid.performanceBondRequired ? 'Yes' : 'No',
        tenderbondrequired: bid.tenderBondRequired ? 'Yes' : 'No',
        quoteValidUntil: bid.submissionDeadline ? this.formatDate(bid.submissionDeadline) : '',
        closureDate: bid.extendedDate ? this.formatDate(bid.extendedDate) : '',
        technicallyQualified: bid.technicallyQualified ? 'Yes' : 'No',
        awardingValue: bid.awardingValue || 0,
        status: bid.status || '',
        regretLetterId: null,
        regretLetterName: null,
        awardLetterId: null,
        awardLetterName: null,
        isBidLocked: false,
        sealedBidMessage: ''
      }));

      // Don't fetch documents on page load - fetch them lazily when user clicks download
      // await this.fetchBidDocuments();

      // Reset selected bidding when data is refreshed
      this.selectedBiddingId = null;

      // Trigger change detection
      this.cdr.detectChanges();
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      console.error(`[Tender Details] Exception while fetching bids:`, errorMessage, error);
      this.receivedBiddings = [];
    }
  }


  openExtendClosureDateModal(): void {
    if (!this.selectedBiddingId) {
      return;
    }
    // Set minimum date to today
    const today = new Date();
    this.minDate = today.toISOString().split('T')[0];
    // Initialize with empty date
    this.extendedClosureDate = '';
    this.showExtendClosureDateModal = true;
  }

  closeExtendClosureDateModal(): void {
    this.showExtendClosureDateModal = false;
    this.extendedClosureDate = '';
  }

  async confirmExtendClosureDate(): Promise<void> {
    if (!this.selectedBiddingId || !this.extendedClosureDate) {
      this.toasterService.showError('Please select a closure date', 'Validation Error');
      return;
    }

    try {
      this._loaderService.showLoader();

      // Format date for API - convert YYYY-MM-DD to ISO string with time
      const dateObj = new Date(this.extendedClosureDate);
      const formattedDate = dateObj.toISOString();

      // API expects BidIds array and NewClosureDate
      const url = `${environment.API_URL}/api/Bids/ExtendBidClosureDate`;
      const payload = {
        bidIds: [this.selectedBiddingId],
        newClosureDate: formattedDate
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      this._loaderService.hideLoader();

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Closure date extended successfully', 'Success');
        this.closeExtendClosureDateModal();
        // Refresh the bids list to show updated dates
        await this.fetchReceivedBiddings();
      } else {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || response?.ErrorMessageEn || 'Failed to extend closure date';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMsg = error?.error?.errorMessageEn || error?.error?.errorMessageAr || error?.message || 'An error occurred while extending closure date';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Practice Details] Error extending closure date:', error);
    }
  }

  // Allow to Participate methods
  openAllowToParticipateModal(): void {
    if (!this.selectedBiddingId) {
      this.toasterService.showError('Please select a bid', 'Validation Error');
      return;
    }
    this.showAllowToParticipateModal = true;
  }

  closeAllowToParticipateModal(): void {
    this.showAllowToParticipateModal = false;
  }

  async confirmAllowToParticipate(): Promise<void> {
    if (!this.selectedBiddingId) {
      this.toasterService.showError('Please select a bid', 'Validation Error');
      return;
    }

    try {
      this._loaderService.showLoader();
      const url = `${environment.API_URL}/api/Bids/UpdateBidStatus`;
      const payload = {
        bidIds: [this.selectedBiddingId],
        status: 'Draft'
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));
      this._loaderService.hideLoader();

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bid status updated to Draft successfully', 'Success');
        this.closeAllowToParticipateModal();
        await this.fetchReceivedBiddings();
      } else {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || response?.ErrorMessageEn || 'Failed to update bid status';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMsg = error?.error?.errorMessageEn || error?.error?.errorMessageAr || error?.message || 'An error occurred while updating bid status';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Practice Details] Error updating bid status:', error);
    }
  }

  // Withdraw methods
  openWithdrawModal(): void {
    if (!this.selectedBiddingId) {
      this.toasterService.showError('Please select a bid', 'Validation Error');
      return;
    }
    this.showWithdrawModal = true;
  }

  closeWithdrawModal(): void {
    this.showWithdrawModal = false;
  }

  async confirmWithdraw(): Promise<void> {
    if (!this.selectedBiddingId) {
      this.toasterService.showError('Please select a bid', 'Validation Error');
      return;
    }

    try {
      this._loaderService.showLoader();
      const url = `${environment.API_URL}/api/Bids/UpdateBidStatus`;
      const payload = {
        bidIds: [this.selectedBiddingId],
        status: 'Withdrawn'
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));
      this._loaderService.hideLoader();

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bid withdrawn successfully', 'Success');
        this.closeWithdrawModal();
        await this.fetchReceivedBiddings();
      } else {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || response?.ErrorMessageEn || 'Failed to withdraw bid';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMsg = error?.error?.errorMessageEn || error?.error?.errorMessageAr || error?.message || 'An error occurred while withdrawing bid';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Practice Details] Error withdrawing bid:', error);
    }
  }

  // Decline methods
  openDeclineModal(): void {
    if (!this.selectedBiddingId) {
      this.toasterService.showError('Please select a bid', 'Validation Error');
      return;
    }
    this.declineRegretLetterFile = null;
    this.showDeclineModal = true;
  }

  closeDeclineModal(): void {
    this.showDeclineModal = false;
    this.declineRegretLetterFile = null;
    this._loaderService.forceHideLoader(); // Force hide loader when dialog closes
  }

  onDeclineFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        this.toasterService.showError('Invalid file type. Please upload PDF, DOC, or DOCX files only.', 'Validation Error');
        event.target.value = '';
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.toasterService.showError('File size exceeds 10MB limit.', 'Validation Error');
        event.target.value = '';
        return;
      }
      this.declineRegretLetterFile = file;
    }
  }

  async confirmDecline(): Promise<void> {
    if (!this.selectedBiddingId || !this.declineRegretLetterFile) {
      this.toasterService.showError('Please select a bid and upload regret letter', 'Validation Error');
      return;
    }

    try {
      // Show loader when user clicks Confirm
      this._loaderService.showLoader();

      // Convert file to base64
      const base64String = await this.convertFileToBase64(this.declineRegretLetterFile);
      const base64Data = base64String.includes(',') ? base64String.split(',')[1] : base64String;

      // Get vendorId from selected bid
      const selectedBid = this.receivedBiddings.find(b => b.biddingNumber === this.selectedBiddingId);
      if (!selectedBid || !selectedBid.vendorId) {
        this._loaderService.hideLoader();
        this.toasterService.showError('Vendor ID not found for selected bid', 'Error');
        return;
      }

      // Upload document using existing endpoint
      const document = {
        processName: 'Bidding',
        attachmentFor: 'RegretLetter',
        fileContent: base64Data,
        name: this.declineRegretLetterFile.name,
        mimeType: this.declineRegretLetterFile.type || this.getMimeTypeFromFileName(this.declineRegretLetterFile.name),
        applicationId: selectedBid.vendorId, // Vendor ID
        processID: this.selectedBiddingId // Bid ID
      };

      const uploadUrl = `${environment.API_URL}/api/Documents/${AppConstants.ACTIONS.UploadDocumnets}`;
      const uploadResponse: any = await lastValueFrom(this.apiService.postByUrl(uploadUrl, [document]));

      console.log('[Practice Details] Upload response:', uploadResponse);

      if (!uploadResponse?.isSuccess && !uploadResponse?.IsSuccess) {
        const errorMsg = uploadResponse?.errorMessageEn || uploadResponse?.errorMessageAr || uploadResponse?.data || 'Failed to upload regret letter';
        this.toasterService.showError(errorMsg, 'Error');
        console.error('[Practice Details] Upload failed:', uploadResponse);
        // Don't hide loader here - it will be hidden when modal closes
        return;
      }

      // Check if data exists and has items
      if (uploadResponse?.data && Array.isArray(uploadResponse.data) && uploadResponse.data.length === 0) {
        this.toasterService.showError('Document upload returned empty result', 'Error');
        console.error('[Practice Details] Upload returned empty data');
        // Don't hide loader here - it will be hidden when modal closes
        return;
      }

      // Then update bid status to Regret
      const url = `${environment.API_URL}/api/Bids/UpdateBidStatus`;
      const payload = {
        bidIds: [this.selectedBiddingId],
        status: 'Regret'
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bid declined successfully', 'Success');
        this.closeDeclineModal(); // This will hide the loader
        await this.fetchReceivedBiddings();
      } else {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || response?.ErrorMessageEn || 'Failed to decline bid';
        this.toasterService.showError(errorMsg, 'Error');
        // Don't hide loader here - it will be hidden when modal closes
      }
    } catch (error: any) {
      const errorMsg = error?.error?.errorMessageEn || error?.error?.errorMessageAr || error?.message || 'An error occurred while declining bid';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Practice Details] Error declining bid:', error);
      // Don't hide loader here - it will be hidden when modal closes
    }
  }

  // Award methods
  openAwardModal(): void {
    if (!this.selectedBiddingId) {
      this.toasterService.showError('Please select a bid', 'Validation Error');
      return;
    }
    this.awardLetterFile = null;
    this.awardRegretLetterFile = null;
    this.showAwardModal = true;
  }

  closeAwardModal(): void {
    this.showAwardModal = false;
    this.awardLetterFile = null;
    this.awardRegretLetterFile = null;
    this._loaderService.forceHideLoader(); // Force hide loader when dialog closes
  }

  onAwardFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf' || !file.name.toLowerCase().endsWith('.pdf')) {
        this.toasterService.showError('Invalid file type. Please upload a PDF award letter.', 'Validation Error');
        event.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.toasterService.showError('File size exceeds 5MB limit.', 'Validation Error');
        event.target.value = '';
        return;
      }
      this.awardLetterFile = file;
    }
  }

  onAwardRegretFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf' || !file.name.toLowerCase().endsWith('.pdf')) {
      this.toasterService.showError('Invalid file type. Please upload a PDF regret letter.', 'Validation Error');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.toasterService.showError('File size exceeds 5MB limit.', 'Validation Error');
      event.target.value = '';
      return;
    }
    this.awardRegretLetterFile = file;
  }

  async confirmAward(): Promise<void> {
    if (!this.selectedBiddingId || !this.awardLetterFile) {
      this.toasterService.showError('Please select a bid and upload award letter', 'Validation Error');
      return;
    }

    try {
      // Show loader when user clicks Confirm
      this._loaderService.showLoader();

      // Convert file to base64
      const base64String = await this.convertFileToBase64(this.awardLetterFile);
      const base64Data = base64String.includes(',') ? base64String.split(',')[1] : base64String;
      const regretBase64String = this.awardRegretLetterFile
        ? await this.convertFileToBase64(this.awardRegretLetterFile)
        : null;

      const url = `${environment.API_URL}/api/Bids/AwardBid`;
      const payload = {
        bidId: this.selectedBiddingId,
        fileContent: base64Data,
        fileName: this.awardLetterFile.name,
        mimeType: 'application/pdf',
        regretFileContent: regretBase64String
          ? (regretBase64String.includes(',') ? regretBase64String.split(',')[1] : regretBase64String)
          : null,
        regretFileName: this.awardRegretLetterFile?.name || null,
        regretMimeType: this.awardRegretLetterFile ? 'application/pdf' : null
      };
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bid awarded successfully. All other bids have been regretted.', 'Success');
        this.closeAwardModal(); // This will hide the loader
        await this.fetchReceivedBiddings();
      } else {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || response?.ErrorMessageEn || 'Failed to award bid';
        this.toasterService.showError(errorMsg, 'Error');
        // Don't hide loader here - it will be hidden when modal closes
      }
    } catch (error: any) {
      const errorMsg = error?.error?.errorMessageEn || error?.error?.errorMessageAr || error?.message || 'An error occurred while awarding bid';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Practice Details] Error awarding bid:', error);
    } finally {
      this._loaderService.forceHideLoader();
    }
  }

  private convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  private getMimeTypeFromFileName(fileName: string): string {
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    };
    return mimeTypes[extension] || 'application/octet-stream';
  }

  async fetchBidDocuments(): Promise<void> {
    const bidsWithDocuments = this.receivedBiddings.filter(b => b.status === 'Regret' || b.status === 'Awarded');

    for (const bid of bidsWithDocuments) {
      try {
        const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
        const documentsDto = {
          applicationId: bid.vendorId, // Vendor ID
          processName: 'Bidding',
          processID: bid.biddingNumber // Bid ID
        };

        const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

        if (documentsResponse?.isSuccess && documentsResponse?.data && Array.isArray(documentsResponse.data)) {
          const documents = documentsResponse.data;

          // Find regret letter
          const regretLetter = documents.find((doc: any) => doc.attachmentFor === 'RegretLetter');
          if (regretLetter) {
            bid.regretLetterId = regretLetter.attachmentId;
            bid.regretLetterName = regretLetter.name;
          }

          // Find award letter
          const awardLetter = documents.find((doc: any) => doc.attachmentFor === 'AwardLetter');
          if (awardLetter) {
            bid.awardLetterId = awardLetter.attachmentId;
            bid.awardLetterName = awardLetter.name;
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch documents for bid ${bid.biddingNumber}:`, error);
      }
    }
  }

  onBidRowClick(row: any): void {
    this.selectBidding(row.biddingNumber);
  }

  onBidLinkClick(event: {field: string, row: any}): void {
    if (event.field === 'vendorAccount') {
      // Handle vendor account link click if needed
      return;
    }
  }

  onBidActionClick(event: {action: string, row: any}): void {
    if (event.action === 'download-regret') {
      this.downloadDocument(event.row.regretLetterId, event.row.regretLetterName, event.row.biddingNumber, event.row.vendorId, 'RegretLetter');
    } else if (event.action === 'download-award') {
      this.downloadDocument(event.row.awardLetterId, event.row.awardLetterName, event.row.biddingNumber, event.row.vendorId, 'AwardLetter');
    }
  }

  isBidRowSelected = (row: any): boolean => {
    return this.selectedBiddingId === row.biddingNumber;
  }

  async downloadDocument(attachmentId: string | null, fileName: string | null, bidId?: string, vendorId?: string, documentType?: 'RegretLetter' | 'AwardLetter'): Promise<void> {
    // If attachmentId is not provided, fetch it lazily
    if (!attachmentId && bidId && vendorId && documentType) {
      try {
        const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
        const documentsDto = {
          applicationId: vendorId,
          processName: 'Bidding',
          processID: bidId
        };

        const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

        if (documentsResponse?.isSuccess && documentsResponse?.data && Array.isArray(documentsResponse.data)) {
          const documents = documentsResponse.data;
          const document = documents.find((doc: any) => doc.attachmentFor === documentType);

          if (document) {
            attachmentId = document.attachmentId || document.AttachmentId;
            fileName = document.name || fileName;

            // Update the bid object to cache the result
            const bid = this.receivedBiddings.find(b => b.biddingNumber === bidId);
            if (bid) {
              if (documentType === 'RegretLetter') {
                bid.regretLetterId = attachmentId;
                bid.regretLetterName = fileName;
              } else if (documentType === 'AwardLetter') {
                bid.awardLetterId = attachmentId;
                bid.awardLetterName = fileName;
              }
            }
          } else {
            this.toasterService.showError(`${documentType === 'RegretLetter' ? 'Regret' : 'Award'} letter not found`, 'Error');
            return;
          }
        } else {
          this.toasterService.showError('Failed to fetch document', 'Error');
          return;
        }
      } catch (error) {
        console.error('Error fetching document:', error);
        this.toasterService.showError('Failed to fetch document', 'Error');
        return;
      }
    }

    if (!attachmentId) {
      this.toasterService.showError('Document not found', 'Error');
      return;
    }

    const downloadUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${attachmentId}`;
    window.open(downloadUrl, '_blank');
  }

  // Check if selected bid status is not Draft
  isBidSubmitted(): boolean {
    if (!this.selectedBiddingId) {
      return false;
    }
    const selectedBid = this.getSelectedBid();
    return selectedBid?.status !== 'Draft' && !selectedBid?.isBidLocked;
  }

  // Bidding Documents Modal methods
  async openBiddingDocumentsModal(): Promise<void> {
    if (!this.selectedBiddingId) {
      this.toasterService.showError('Please select a bid', 'Validation Error');
      return;
    }

    const selectedBid = this.getSelectedBid();
    if (selectedBid?.isBidLocked) {
      this.toasterService.showError(selectedBid.sealedBidMessage || 'Bid is sealed and bidding documents cannot be opened yet.', 'Bid Sealed');
      return;
    }

    if (!selectedBid || selectedBid.status === 'Draft') {
      this.toasterService.showError('Bidding documents are only available for non-draft bids', 'Validation Error');
      return;
    }

    this.showBiddingDocumentsModal = true;
    this.isLoadingBiddingDocuments = true;

    // Reset documents
    this.biddingDocuments = {
      commercialProposal: [],
      technicalProposal: [],
      tenderBond: [],
      performanceBond: [],
      otherDocuments: []
    };

    // Trigger change detection to show loading state
    this.cdr.detectChanges();

    try {
      await this.fetchBiddingDocumentsForModal();
    } catch (error) {
      console.error('[Practice Details] Error fetching bidding documents:', error);
      this.toasterService.showError('Failed to load bidding documents', 'Error');
    } finally {
      this.isLoadingBiddingDocuments = false;
      // Trigger change detection to hide loading state
      this.cdr.detectChanges();
    }
  }

  closeBiddingDocumentsModal(): void {
    this.showBiddingDocumentsModal = false;
    this.biddingDocuments = {
      commercialProposal: [],
      technicalProposal: [],
      tenderBond: [],
      performanceBond: [],
      otherDocuments: []
    };
  }

  async fetchBiddingDocumentsForModal(): Promise<void> {
    if (!this.selectedBiddingId) {
      return;
    }

    const selectedBid = this.receivedBiddings.find(b => b.biddingNumber === this.selectedBiddingId);
    if (!selectedBid || !selectedBid.vendorId) {
      return;
    }

    try {
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: selectedBid.vendorId, // Vendor ID
        processName: 'Bidding',
        processID: this.selectedBiddingId // Bid ID
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      if (documentsResponse?.isSuccess && documentsResponse?.data && Array.isArray(documentsResponse.data)) {
        const documents = documentsResponse.data;

        // Fetch file sizes for each document asynchronously (only when modal is opened)
        const documentsWithSizes = await Promise.all(documents.map(async (doc: any) => {
          if (doc.attachmentId || doc.AttachmentId) {
            try {
              // Get file size from blob storage via backend
              const fileSizeUrl = `${environment.API_URL}/api/Documents/GetDocumentFileSize/${doc.attachmentId || doc.AttachmentId}`;
              const sizeResponse: any = await lastValueFrom(this.apiService.getByUrl(fileSizeUrl)).catch(() => null);
              if (sizeResponse?.isSuccess && sizeResponse?.data) {
                doc.fileSize = sizeResponse.data;
                doc.FileSize = sizeResponse.data;
              }
            } catch (error) {
              // Silently fail - file size is optional
              console.warn(`Failed to get file size for document ${doc.attachmentId || doc.AttachmentId}:`, error);
            }
          }
          return doc;
        }));

        const normalizeAttachmentFor = (doc: any) => (doc?.attachmentFor || doc?.AttachmentFor || '').toString().trim().toLowerCase().replace(/\s+/g, '');
        const matchesAttachmentFor = (doc: any, types: string[]) => types.includes(normalizeAttachmentFor(doc));

        this.biddingDocuments.commercialProposal = documentsWithSizes.filter((doc: any) =>
          matchesAttachmentFor(doc, ['commercialproposal', 'commercial'])
        );

        this.biddingDocuments.technicalProposal = documentsWithSizes.filter((doc: any) =>
          matchesAttachmentFor(doc, ['technicalproposal', 'technical'])
        );

        this.biddingDocuments.tenderBond = documentsWithSizes.filter((doc: any) =>
          matchesAttachmentFor(doc, ['tenderbond'])
        );

        this.biddingDocuments.performanceBond = documentsWithSizes.filter((doc: any) =>
          matchesAttachmentFor(doc, ['performancebond'])
        );

        const knownTypes = ['commercialproposal', 'commercial', 'technicalproposal', 'technical',
                           'tenderbond', 'performancebond', 'regretletter', 'awardletter'];
        this.biddingDocuments.otherDocuments = documentsWithSizes.filter((doc: any) =>
          !knownTypes.includes(normalizeAttachmentFor(doc))
        );
        // Trigger change detection after updating documents
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('[Practice Details] Error fetching bidding documents:', error);
      throw error;
    }
  }

  formatFileSize(bytes: number | null | undefined): string {
    if (!bytes || bytes === 0) {
      return '0.000 MB';
    }
    const mb = bytes / (1024 * 1024);
    // Always show 3 decimal places, even for very small files
    return `${mb.toFixed(3)} MB`;
  }

  previewDocument(attachmentId: string, fileName: string): void {
    this.downloadDocument(attachmentId, fileName);
  }
  isBiddingDocumentSealed(doc: any): boolean {
    return false;
  }

  canPreviewBiddingDocument(doc: any): boolean {
    return !!this.getBiddingDocumentAttachmentId(doc) && !this.isBiddingDocumentSealed(doc);
  }

  getBiddingDocumentName(doc: any): string {
    return doc?.name || doc?.Name || 'Document';
  }

  getBiddingDocumentAttachmentId(doc: any): string {
    return doc?.attachmentId || doc?.AttachmentId || '';
  }

  getBiddingDocumentSize(doc: any): number {
    return doc?.fileSize || doc?.FileSize || 0;
  }

  getBiddingDocumentDate(doc: any): string | null {
    return doc?.createdAt || doc?.CreatedAt || doc?.createdDate || doc?.CreatedDate || null;
  }

  getBiddingDocumentExpiryDate(doc: any): string | null {
    return doc?.documentExpiryDate || doc?.DocumentExpiryDate || doc?.expiryDate || doc?.ExpiryDate || null;
  }

  isSelectedBidLocked(): boolean {
    return false;
  }

  private getSelectedBid(): any | null {
    if (!this.selectedBiddingId) {
      return null;
    }
    return this.receivedBiddings.find(b => b.biddingNumber === this.selectedBiddingId) || null;
  }

  private isTechnicalOrCommercialBidDocument(doc: any): boolean {
    const attachmentFor = (doc?.attachmentFor || doc?.AttachmentFor || '').toString().trim().toLowerCase().replace(/\s+/g, '');
    return attachmentFor === 'commercialproposal' ||
      attachmentFor === 'commercial' ||
      attachmentFor === 'technicalproposal' ||
      attachmentFor === 'technical';
  }

  private isBidDocumentUnsealed(doc: any): boolean {
    const rawValue = doc?.isUnsealed ?? doc?.IsUnsealed ?? doc?.unsealed ?? doc?.Unsealed ?? doc?.documentUnsealed ?? doc?.DocumentUnsealed;
    if (typeof rawValue === 'boolean') {
      return rawValue;
    }

    const status = (rawValue ?? doc?.sealStatus ?? doc?.SealStatus ?? doc?.documentStatus ?? doc?.DocumentStatus ?? doc?.status ?? doc?.Status ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return status === 'unsealed' || status === 'open' || status === 'opened';
  }

  private async applyBidSealState(): Promise<void> {
    await Promise.all(this.receivedBiddings.map(async (bid: any) => {
      bid.isBidLocked = false;
      bid.sealedBidMessage = '';

      if (!bid?.biddingNumber || !bid?.vendorId || bid.status === 'Draft') {
        return;
      }

      try {
        const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
        const documentsDto = {
          applicationId: bid.vendorId,
          processName: 'Bidding',
          processID: bid.biddingNumber
        };

        const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));
        const documents = documentsResponse?.data && Array.isArray(documentsResponse.data) ? documentsResponse.data : [];
        const proposalDocuments = documents.filter((doc: any) => this.isTechnicalOrCommercialBidDocument(doc));

        bid.isBidLocked = proposalDocuments.length > 0 && proposalDocuments.some((doc: any) => !this.isBidDocumentUnsealed(doc));
        bid.sealedBidMessage = bid.isBidLocked ? 'Technical/commercial bid documents are still sealed.' : '';
      } catch (error) {
        bid.isBidLocked = true;
        bid.sealedBidMessage = 'Unable to verify bid document seal status.';
        console.warn(`[Tender Details] Failed to verify seal status for bid ${bid.biddingNumber}:`, error);
      }
    }));
  }
  // Helper methods - using TenderHelperService
  private getPropertyValue = (obj: any, ...keys: string[]) => this.tenderHelper.getPropertyValue(obj, ...keys);
  private getTrimmedValue = (value: any) => this.tenderHelper.getTrimmedValue(value);

  private async loadDataFromAPI(
    endpoint: string,
    propertyKeys: string[],
    apiDataProperty: string,
    displayDataProperty: string,
    fallbackData?: string[]
  ): Promise<void> {
    try {
      const url = `${environment.API_URL}${endpoint}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const dataList = this.tenderHelper.extractArrayFromResponse(response);

      if (dataList.length > 0) {
        (this as any)[apiDataProperty] = dataList;
        (this as any)[displayDataProperty] = dataList
          .map((item: any) => this.getPropertyValue(item, ...propertyKeys))
          .filter((value: string) => value !== '');
      } else if (fallbackData) {
        (this as any)[displayDataProperty] = fallbackData;
      }
    } catch (error) {
      console.error(`[Tender Details] Error loading data from ${endpoint}:`, error);
      if (fallbackData) {
        (this as any)[displayDataProperty] = fallbackData;
      }
    }
  }

  private getValidValue = (inputValue: any, apiData: any[], propertyKeys: string[]) =>
    this.tenderHelper.getValidValue(inputValue, apiData, propertyKeys, this.getPropertyValue);

  private getValidCurrencyId = (currencyValue: any): string | null =>
    this.getValidValue(currencyValue, this.currenciesFromAPI, ['currencyId', 'CurrencyId', 'currency', 'Currency']);

  private getValidPRNumber = (prValue: any): string | null =>
    this.getValidValue(prValue, this.purchaseRequisitionsFromAPI, ['prNumber', 'PRNumber', 'pRNumber', 'PrNumber']);

  private getValidDepartmentId(departmentValue: any): string | null {
    const selectedDepartment = this.getTrimmedValue(departmentValue);
    if (!selectedDepartment) return null;

    const matchedDepartment = this.euDepartmentsFromAPI.find((department: any) => {
      const departmentId = this.getTrimmedValue(this.getPropertyValue(department, 'departmentId', 'DepartmentId'));
      const departmentName = this.getTrimmedValue(this.getPropertyValue(department, 'departmentName', 'DepartmentName', 'name', 'Name'));
      return departmentId === selectedDepartment || departmentName === selectedDepartment;
    });

    return matchedDepartment
      ? this.getTrimmedValue(this.getPropertyValue(matchedDepartment, 'departmentId', 'DepartmentId'))
      : selectedDepartment;
  }

  private getDepartmentDisplayName(departmentValue: any): string {
    const selectedDepartment = this.getTrimmedValue(departmentValue);
    if (!selectedDepartment) return '';

    const matchedDepartment = this.euDepartmentsFromAPI.find((department: any) => {
      const departmentId = this.getTrimmedValue(this.getPropertyValue(department, 'departmentId', 'DepartmentId'));
      const departmentName = this.getTrimmedValue(this.getPropertyValue(department, 'departmentName', 'DepartmentName', 'name', 'Name'));
      return departmentId === selectedDepartment || departmentName === selectedDepartment;
    });

    return matchedDepartment
      ? this.getTrimmedValue(this.getPropertyValue(matchedDepartment, 'departmentName', 'DepartmentName', 'name', 'Name')) || selectedDepartment
      : selectedDepartment;
  }

  async loadCurrencies(): Promise<void> {
    try {
      // Load all lookups from local storage (via ApiService)
      await this.apiService.loadAllLookups();

      // Currencies are loaded from local storage (allLookups_v2_anon) in ApiService
      this.currenciesFromAPI = this.apiService.currencies || [];

      // Extract currency values using the same property keys as before
      this.currencies = (this.apiService.currencies || [])
        .map((item: any) => this.getPropertyValue(item, 'currencyId', 'CurrencyId', 'currency', 'Currency'))
        .filter((value: string) => value !== '');
    } catch (error) {
      console.error('[Tender Details] Error loading currencies from local storage:', error);
    }
  }

  async loadPurchaseRequisitions(): Promise<void> {
    await this.loadDataFromAPI(
      `/api/PurchaseRequisition/${AppConstants.ACTIONS.GetAllApprovedPRsForTenderScope}?scope=0`,
      ['prNumber', 'PRNumber', 'pRNumber', 'PrNumber'],
      'purchaseRequisitionsFromAPI',
      'purchaseRequisitions'
    );
  }

  async loadVendors(): Promise<void> {
    try {
      const url = `${environment.API_URL}/api/Vendor/${AppConstants.ACTIONS.GetAllVendorsInfo}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const vendorsList = this.tenderHelper.extractArrayFromResponse(response);

      if (vendorsList.length > 0) {
        this.vendorsFromAPI = vendorsList;
        // Extract vendor accounts and names - prioritize vendorID from new API structure
        this.vendorDisplayToId = {};
        this.vendorAccounts = vendorsList
          .map((vendor: any) => {
            const vendorId = this.getTrimmedValue(this.getPropertyValue(vendor, 'vendorID', 'vendorId', 'VendorId', 'VendorID', 'id', 'Id'));
            const vendorDisplay = this.getVendorDisplayName(vendor, vendorId);
            if (vendorDisplay && vendorId) {
              this.vendorDisplayToId[vendorDisplay] = vendorId;
            }
            return vendorDisplay;
          })
          .filter((value: string) => value !== '');

        // Build vendorNames mapping using fullName - handle empty strings and whitespace
        this.vendorNames = {};
        vendorsList.forEach((vendor: any) => {
          const vendorId = this.getTrimmedValue(this.getPropertyValue(vendor, 'vendorID', 'vendorId', 'VendorId', 'VendorID', 'id', 'Id'));
          const fullName = this.getVendorNameOnly(vendor);
          // Only add to mapping if vendorId exists and fullName is not empty/whitespace
          if (vendorId && fullName) {
            this.vendorNames[vendorId] = fullName;
          }
        });
      }
    } catch (error) {
      console.error('[Tender Details] Error loading vendors:', error);
    }
  }

  async loadDepartments(): Promise<void> {
    await this.loadDataFromAPI(
      `/api/Lookups/departments`,
      ['departmentName', 'DepartmentName', 'name', 'Name'],
      'euDepartmentsFromAPI',
      'euDepartments'
    );

    const currentDepartment = this.tenderForm.get('euDepartment')?.value;
    const displayDepartment = this.getDepartmentDisplayName(currentDepartment);
    if (displayDepartment && displayDepartment !== currentDepartment) {
      this.tenderForm.get('euDepartment')?.setValue(displayDepartment, { emitEvent: false });
    }
  }

  // Format date methods - using TenderHelperService
  public formatDate = (dateValue: any) => this.tenderHelper.formatDateForDisplay(dateValue);
  public formatDateTime = (dateValue: any) => this.tenderHelper.formatDateTimeForDisplay(dateValue);
  private formatDateForAPI = (dateValue: any) => this.tenderHelper.formatDateForAPI(dateValue);
  private extractEmail = (contactDetails: string) => this.tenderHelper.extractEmail(contactDetails);
  private extractPhone = (contactDetails: string) => this.tenderHelper.extractPhone(contactDetails);
  private convertBooleanToYesNo = (value: any) => this.tenderHelper.convertBooleanToYesNo(value);
  private convertYesNoToTrueFalse = (value: any) => this.tenderHelper.convertYesNoToTrueFalse(value);
  private normalizeBooleanString = (value: any) => this.tenderHelper.normalizeBooleanString(value);
  private parseNumber = (value: any) => this.tenderHelper.parseNumber(value);
  private mapFormArrayToPayload(
    formArray: FormArray,
    mapper: (control: any) => any
  ): any[] {
    return formArray.controls.map(mapper);
  }

  private prepareTenderPayload(): any {
    const formValue = this.tenderForm.getRawValue();
    const immutableFormValue = this.isEditMode && this.originalFormData ? this.originalFormData : formValue;
    const version = this.getTrimmedValue(formValue.version) || '1';

    if (!version) {
      throw new Error('Version is required');
    }

    return {
      tenderNumber: this.getTrimmedValue(formValue.tenderNumber) || null,
      version: version,
      tenderName: this.getTrimmedValue(formValue.tenderName) || null,
      status: this.getTrimmedValue(formValue.status) || 'Draft',
      tenderType: this.getTrimmedValue(formValue.tenderType) || null,
      rfpRfq: this.getTrimmedValue(formValue.rfpRfq) || null,
      tenderClassification: this.getTrimmedValue(formValue.tenderClassification) || null,
      company: this.getTrimmedValue(formValue.company) || null,
      euDepartment: this.getValidDepartmentId(formValue.euDepartment) || null,
      euDepartmentSpoc: this.getTrimmedValue(formValue.euDepartmentSpoc) || null,
      purchaseRequisition: this.getValidPRNumber(immutableFormValue.purchaseRequisition) || this.getTrimmedValue(immutableFormValue.purchaseRequisition),
      managedBySpoc: this.getTrimmedValue(formValue.managedBySpoc),
      managedByContactDetails: this.getTrimmedValue(formValue.managedByContactDetails),
      currency: this.getValidCurrencyId(formValue.currency) || this.getTrimmedValue(formValue.currency),
      tenderValue: this.parseNumber(immutableFormValue.tenderValue),
      tenderValueInQAR: this.parseNumber(immutableFormValue.tenderValueInQAR),
      awaitingValue: this.parseNumber(formValue.awaitingValue),
      awardingValueInQAR: this.parseNumber(formValue.awardingValueInQAR),
      startDateOfTender: this.formatDateForAPI(formValue.startDateOfTender),
      acceptanceDeadline: this.formatDateForAPI(formValue.acceptanceDeadline),
      extendedAcceptanceDeadline: this.formatDateForAPI(formValue.extendedAcceptanceDeadline),
      note: this.getTrimmedValue(formValue.note) || null,
      recommendationJustification: this.getTrimmedValue(immutableFormValue.recommendationJustification) || null,
      isJoiningFeeRequired: this.convertYesNoToTrueFalse(formValue.isJoiningFeeRequired),
      joiningFee: this.parseNumber(formValue.joiningFee),
      bidClosureDate: this.formatDateForAPI(formValue.bidClosureDate),
      backgroundAndSOW: this.getTrimmedValue(formValue.backgroundAndSOW) || null,
      technicalEvaluation: this.getTrimmedValue(formValue.technicalEvaluation) || null,
      commercialEvaluation: this.getTrimmedValue(formValue.commercialEvaluation) || null,
      tenderBondRequired: this.convertYesNoToTrueFalse(formValue.tenderBondRequired),
      tenderBondDetails: this.getTrimmedValue(formValue.tenderBondDetails) || null,
      tenderBondValidityDate: this.formatDateForAPI(formValue.tenderBondValidityDate),
      isDraft: this.normalizeBooleanString(formValue.isDraft),
      performanceBondRequired: this.convertYesNoToTrueFalse(formValue.performanceBondRequired),
      performanceBondDetails: this.getTrimmedValue(formValue.performanceBondDetails) || null,
      performanceBondValidityDate: this.formatDateForAPI(formValue.performanceBondValidityDate),
      selectedVendors: this.isPrivateTender ? this.mapFormArrayToPayload(
        this.selectedVendorsFormArray,
        (control: any) => ({
          vendorId: this.getTrimmedValue(this.vendorDisplayToId[control.get('vendorAccount')?.value] || control.get('vendorAccount')?.value),
          vendorName: this.getTrimmedValue(control.get('vendorName')?.value)
        })
      ).filter((v: any) => v.vendorId !== '') : [],
      // Evaluation criteria from shared component
      evaluationType: this.currentEvaluationData?.evaluationType || 'Separate',
      technicalWeightage: this.currentEvaluationData?.technicalWeightage || 100.00,
      commercialWeightage: this.currentEvaluationData?.commercialWeightage || 100.00,
      technicalEvaluationCriteria: (this.currentEvaluationData?.technicalEvaluationCriteria || []).map(item => ({
        description: this.getTrimmedValue(item.description),
        weightage: item.weightage || 0
      })),
      commercialEvaluationCriteria: (this.currentEvaluationData?.commercialEvaluationCriteria || []).map(item => ({
        description: this.getTrimmedValue(item.description),
        weightage: item.weightage || 0
      })),
      tenderDocuments: this.tenderDocuments
        .filter((doc: any) => doc.isNew && doc.fileContent && this.getTrimmedValue(doc.name))
        .map((doc: any) => ({
          name: this.getTrimmedValue(doc.name),
          fileContent: doc.fileContent,
          mimeType: doc.mimeType || this.getMimeTypeFromFileName(doc.name),
          attachmentFor: 'Tender',
          additionalDetails: this.getTrimmedValue(doc.additionalDetails) || ''
        })),
      deletedTenderDocumentIds: this.deletedTenderDocumentIds
    };
  }
  private isSuccessResponse = (response: any) => this.tenderHelper.isSuccessResponse(response);
  private getResponseErrorMessage = (response: any, defaultMessage: string) =>
    this.tenderHelper.getResponseErrorMessage(response, defaultMessage);
  private getErrorMessage = (error: any) => this.tenderHelper.getErrorMessage(error);

  private getVendorDisplayName(vendor: any, vendorId?: string): string {
    const normalizedVendorId = this.getTrimmedValue(vendorId || this.getPropertyValue(vendor, 'vendorID', 'vendorId', 'VendorId', 'VendorID', 'id', 'Id'));
    const vendorName = this.getVendorNameOnly(vendor);

    if (normalizedVendorId && vendorName) {
      return `${normalizedVendorId} - ${vendorName}`;
    }

    return vendorName || normalizedVendorId || '';
  }

  private getVendorNameOnly(vendor: any): string {
    return this.getTrimmedValue(this.getPropertyValue(vendor, 'vendorName', 'VendorName', 'fullName', 'FullName', 'fullname', 'Fullname', 'name', 'Name'));
  }

  private getVendorDisplayById(vendorId?: string, fallbackName?: string): string {
    const normalizedVendorId = this.getTrimmedValue(vendorId);
    const vendor = this.vendorsFromAPI.find((v: any) => {
      const id = this.getTrimmedValue(this.getPropertyValue(v, 'vendorID', 'vendorId', 'VendorId', 'VendorID', 'id', 'Id'));
      return id === normalizedVendorId;
    });

    if (vendor) {
      return this.getVendorDisplayName(vendor, normalizedVendorId);
    }

    const vendorName = this.getTrimmedValue(fallbackName);
    return normalizedVendorId && vendorName ? `${normalizedVendorId} - ${vendorName}` : normalizedVendorId || vendorName || '';
  }

  private createVendorGroup(vendorId?: string, vendorName?: string): FormGroup {
    const normalizedVendorId = this.getTrimmedValue(vendorId);
    const initialVendorAccount = this.getVendorDisplayById(normalizedVendorId, vendorName);
    const initialVendorName = this.getTrimmedValue(vendorName) || this.vendorNames[normalizedVendorId] || '';

    const vendorGroup = this._fb.group({
      vendorAccount: [initialVendorAccount],
      vendorName: [initialVendorName]
    });

    vendorGroup.get('vendorAccount')?.valueChanges.subscribe(account => {
      const selectedVendorId = account ? (this.vendorDisplayToId[account] || account) : '';
      const vendor = this.vendorsFromAPI.find((v: any) => {
        const vId = this.getTrimmedValue(this.getPropertyValue(v, 'vendorId', 'VendorId', 'vendorID', 'VendorID', 'id', 'Id'));
        return vId === selectedVendorId || this.getVendorDisplayName(v, vId) === account;
      });
      const vendorName = vendor ? this.getVendorNameOnly(vendor) : (selectedVendorId ? (this.vendorNames[selectedVendorId] || '') : '');

      vendorGroup.patchValue({ vendorName }, { emitEvent: false });
    });

    return vendorGroup;
  }

  addSelectedVendor(): void {
    const vendorGroup = this.createVendorGroup();
    if (!this.isEditMode) {
      vendorGroup.disable();
    }
    this.selectedVendorsFormArray.push(vendorGroup);
    this.cdr.detectChanges();
  }

  private canRemoveItem(formArray: FormArray): boolean {
    return formArray.length > 1;
  }

  removeSelectedVendor(index: number): void {
    this.selectedVendorsFormArray.removeAt(index);
    this.cdr.detectChanges();
  }

  removeAllSelectedVendors(): void {
    while (this.selectedVendorsFormArray.length > 0) {
      this.selectedVendorsFormArray.removeAt(0);
    }
    this.cdr.detectChanges();
  }

  private populateSelectedVendors(vendors: any[]): void {
    // Clear existing vendors
    while (this.selectedVendorsFormArray.length !== 0) {
      this.selectedVendorsFormArray.removeAt(0);
    }

    // Add vendors from API
    vendors.forEach((vendor: any) => {
      const vendorId = this.getTrimmedValue(vendor.vendorId || vendor.vendorID || vendor.VendorId || vendor.VendorID);
      const vendorName = this.getTrimmedValue(vendor.vendorName || vendor.VendorName || vendor.fullName || vendor.FullName || vendor.name || vendor.Name);

      // Create vendor group with display account and name-only field.
      const vendorGroup = this.createVendorGroup(vendorId, vendorName);

      if (!this.isEditMode) {
        vendorGroup.disable(); // Disable for read-only
      }
      this.selectedVendorsFormArray.push(vendorGroup);
    });

    // Trigger change detection to update the view
    this.cdr.detectChanges();
  }

  private updateSerialNumbers(formArray: FormArray): void {
    formArray.controls.forEach((control, i) => {
      control.patchValue({ serialNumber: i + 1 }, { emitEvent: false });
    });
  }

  private populateEvaluationCriteria(tenderData: any): void {
    // Populate evaluation type and weightages
    const evaluationType = this.getTrimmedValue(tenderData.evaluationType || tenderData.EvaluationType) || 'Separate';
    const technicalWeightage = tenderData.technicalWeightage || tenderData.TechnicalWeightage || 100.00;
    const commercialWeightage = tenderData.commercialWeightage || tenderData.CommercialWeightage || 100.00;

    // Parse weightages
    const techWeight = typeof technicalWeightage === 'string' ? parseFloat(technicalWeightage) : Number(technicalWeightage);
    const commWeight = typeof commercialWeightage === 'string' ? parseFloat(commercialWeightage) : Number(commercialWeightage);

    // First try to get from direct properties (from API mapping)
    let technicalCriteria: Array<{ description: string; weightage: number }> = [];
    let commercialCriteria: Array<{ description: string; weightage: number }> = [];

    if (tenderData.technicalEvaluationCriteria && Array.isArray(tenderData.technicalEvaluationCriteria)) {
      technicalCriteria = tenderData.technicalEvaluationCriteria.map((item: any) => ({
        description: this.getTrimmedValue(item.description || item.Description || ''),
        weightage: item.weightage || item.Weightage || 0
      })).filter((item: any) => item.description);
    }

    if (tenderData.commercialEvaluationCriteria && Array.isArray(tenderData.commercialEvaluationCriteria)) {
      commercialCriteria = tenderData.commercialEvaluationCriteria.map((item: any) => ({
        description: this.getTrimmedValue(item.description || item.Description || ''),
        weightage: item.weightage || item.Weightage || 0
      })).filter((item: any) => item.description);
    }

    // Update evaluation criteria data for shared component
    this.evaluationCriteriaData = {
      evaluationType: evaluationType,
      technicalWeightage: isNaN(techWeight) ? 100.00 : techWeight,
      commercialWeightage: isNaN(commWeight) ? 100.00 : commWeight,
      technicalEvaluationCriteria: technicalCriteria,
      commercialEvaluationCriteria: commercialCriteria
    };
  }

  editTender(): void {
    if (!this.canEditTender) {
      this.isEditMode = false;
      this.tenderForm.disable();
      this.disableDropdownFields();
      this.selectedVendorsFormArray.controls.forEach(control => control.disable());
      this.toasterService.showError('Only draft records can be edited', 'Edit not allowed');
      return;
    }

    this.isEditMode = true;
    // Save original form data for cancel
    this.originalFormData = JSON.parse(JSON.stringify(this.tenderForm.getRawValue()));
    // Enable form controls
    this.tenderForm.enable();
    // Enable dropdown fields explicitly
    this.enableDropdownFields();
    this.tenderForm.get('purchaseRequisition')?.disable();
    this.tenderForm.get('tenderValue')?.disable();
    this.tenderForm.get('tenderValueInQAR')?.disable();
    this.tenderForm.get('recommendationJustification')?.disable();
    // Enable FormArrays - only enable description, type, and maximumMarks for evaluation items
    // For vendors, enable vendorAccount but keep vendorName readonly
    this.selectedVendorsFormArray.controls.forEach(control => {
      control.get('vendorAccount')?.enable();
      // vendorName stays readonly/disabled
    });
    this.cdr.detectChanges();
  }

  private disableDropdownFields(): void {
    // Disable dropdown fields when in view mode
    const dropdownFields = [
      'tenderType',
      'rfpRfq',
      'tenderClassification',
      'euDepartment',
      'euDepartmentSpoc',
      'managedBySpoc',
      'currency'
    ];

    dropdownFields.forEach(field => {
      const control = this.tenderForm.get(field);
      if (control) {
        control.disable();
      }
    });
  }

  private enableDropdownFields(): void {
    // Enable dropdown fields when in edit mode
    const dropdownFields = [
      'tenderType',
      'rfpRfq',
      'tenderClassification',
      'euDepartment',
      'euDepartmentSpoc',
      'managedBySpoc',
      'currency'
    ];

    dropdownFields.forEach(field => {
      const control = this.tenderForm.get(field);
      if (control) {
        control.enable();
      }
    });
  }

  cancelEdit(): void {
    this.isEditMode = false;
    // Restore original form data
    if (this.originalFormData) {
      this.tenderForm.patchValue(this.originalFormData);
    }
    // Disable form controls
    this.tenderForm.disable();
    // Disable dropdown fields explicitly
    this.disableDropdownFields();
    // Disable FormArrays
    this.selectedVendorsFormArray.controls.forEach(control => control.disable());
    this.originalFormData = null;
    this.deletedTenderDocumentIds = [];
    if (this.activeTab === 'Documents') {
      this.loadTenderDocuments();
    }
    this.cdr.detectChanges();
  }

  async updateTender(): Promise<void> {
    if (!this.tenderForm.valid) {
      this.toasterService.showError('Please fill all required fields', 'Validation Error');
      return;
    }

    try {
      const payload = this.prepareTenderPayload();
      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.AdminCreateOrUpdateTenderNew}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      if (this.isSuccessResponse(response)) {
        this._loaderService.hideLoader();
        this.toasterService.showSuccess('Tender updated successfully', 'Success');
        this.isEditMode = false;
        this.tenderForm.disable();
        // Disable dropdown fields explicitly
        this.disableDropdownFields();
        this.selectedVendorsFormArray.controls.forEach(control => control.disable());
        this.deletedTenderDocumentIds = [];
        // Reload tender data
        await this.fetchTenderDetails();
        if (this.activeTab === 'Documents') {
          await this.loadTenderDocuments();
        }
      } else {
        this._loaderService.hideLoader();
        this.toasterService.showError(
          this.getResponseErrorMessage(response, 'Failed to update tender'),
          'Error'
        );
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      this.toasterService.showError(
        this.getErrorMessage(error) || 'An error occurred while updating the tender',
        'Error'
      );
      console.error('[Tender Details] Error updating tender:', error);
    }
  }

  assignEvaluators(): void {
    const queryParams: any = {
      tenderId: this.tenderId || this.tenderDetails?.tenderId || 'T-000001',
      vendorId: '',
      evaluationType: ''
    };

    this._router.navigate(
      [`/${AppConstants.ROUTES.EVALUATIONS}/${AppConstants.ROUTES.ASSIGN_EVALUATORS}`],
      { queryParams: queryParams }
    );
  }

  startVoting(): void {
    // TODO: Implement start voting - not used in practice
  }

  navigateToBidDetails(): void {
    if (!this.selectedBiddingId) {
      return;
    }

    const selectedBid = this.getSelectedBid();
    if (selectedBid?.isBidLocked) {
      this.toasterService.showError(selectedBid.sealedBidMessage || 'Bid is sealed and cannot be opened yet.', 'Bid Sealed');
      return;
    }
    // Navigate to bid details page with the selected bid ID
    this._router.navigate([
      `/${AppConstants.ROUTES.BID}/${AppConstants.ROUTES.BIDDETAILS}/${this.selectedBiddingId}`
    ]);
  }

  selectBidding(biddingId: string): void {
    // Toggle selection: if clicking the same bidding, deselect it
    if (this.selectedBiddingId === biddingId) {
      this.selectedBiddingId = null;
    } else {
      this.selectedBiddingId = biddingId;
    }
    this.cdr.detectChanges();
  }

  onEvaluationCriteriaChange(data: EvaluationCriteriaData): void {
    this._latestEvaluationData = data;
  }

  private get currentEvaluationData(): EvaluationCriteriaData | undefined {
    return this._latestEvaluationData || this.evaluationCriteriaData;
  }

  // Wrapper getters that read from shared component data
  get technicalWeightage(): number {
    return this.currentEvaluationData?.technicalWeightage || 100.00;
  }

  get commercialWeightage(): number {
    return this.currentEvaluationData?.commercialWeightage || 100.00;
  }

  get totalWeightage(): number {
    return this.technicalWeightage + this.commercialWeightage;
  }

  get isJointEvaluation(): boolean {
    return this.currentEvaluationData?.evaluationType === 'Joint';
  }

  get isSeparateEvaluation(): boolean {
    return this.currentEvaluationData?.evaluationType === 'Separate';
  }

  getWorkflowActionButtonText(): string {
    return this.getActiveWorkflowStepLabel() || this.workflowAction;
  }
  handleWorkflowAction(): void {
    const activeStepRole = this.getActiveWorkflowResponsibleRole();

    if (!this.canPerformWorkflowAction()) {
      this.toasterService.showError('Only ' + (activeStepRole || 'the configured stage role') + ' can perform this workflow step for practices.', 'Permission Denied');
      return;
    }

    this.pendingAction = this.getWorkflowActionButtonText() as any;
    this.showConfirmDialog = true;
  }
  handleRequestChangeAction(): void {
    const activeStepRole = this.getActiveWorkflowResponsibleRole();

    if (!this.canRequestChangeWorkflowAction()) {
      this.toasterService.showError('Only ' + (activeStepRole || 'the configured stage role') + ' can request changes for practices.', 'Permission Denied');
      return;
    }

    this.pendingAction = 'Request Change';
    this.showConfirmDialog = true;
  }
  async confirmWorkflowAction(): Promise<void> {
    if (!this.pendingAction) return;

    const action = this.pendingAction;
    const activeStep = this.getActiveWorkflowStep();
    const currentWorkflowStatus = (this.workflowStatus || '').toString().toLowerCase().replace(/\s+/g, '');
    const isStageOneSubmission = Number(activeStep?.stageNumber ?? -1) === 1 && (!currentWorkflowStatus || currentWorkflowStatus === 'notsubmitted' || currentWorkflowStatus === '0');
    const normalizedAction = isStageOneSubmission ? 'submission' : this.normalizeWorkflowStepName(action);
    const comments = this.workflowComments;

    this.showConfirmDialog = false;
    this.pendingAction = null;

    try {
      if (action === 'Request Change') {
        await this.requestTenderChange(comments);
      } else if (normalizedAction === 'submission') {
        await this.submitTender();
      } else if (normalizedAction === 'publishing') {
        await this.publishTender();
      } else {
        await this.approveTender();
      }
    } finally {
      this.workflowComments = '';
    }
  }
  cancelWorkflowAction(): void {
    this.showConfirmDialog = false;
    this.pendingAction = null;
    this.workflowComments = '';
  }

  getConfirmDialogMessage(): string {
    const action = this.pendingAction || this.workflowAction;

    if (action === 'Request Change') {
      return 'Are you sure you want to request changes for this practice?';
    }

    return `Are you sure you want to ${action} this practice?`;
  }

  async submitTender(): Promise<void> {
    try {
      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.SubmitTender}`;
      const payload = {
        TenderNumber: this.tenderId
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));
      this._loaderService.hideLoader();

      if (response && (response.IsSuccess || response.isSuccess || response.data)) {
        this.toasterService.showSuccess('Tender submitted successfully', 'Success');
        // Refresh to get updated workflow state - user info will come from backend
        await this.fetchTenderDetails();
        this.initializeWorkflowData(); // Reinitialize workflow timeline
      } else {
        const errorMsg = response?.ErrorMessageEn || response?.errorMessageEn || response?.ErrorMessage || 'Failed to submit tender';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMsg = error?.error?.ErrorMessageEn || error?.message || 'An error occurred while submitting the tender';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Tender Details] Error submitting tender:', error);
    }
  }

  async approveTender(): Promise<void> {
    try {
      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.ApproveTender}`;
      const payload = {
        TenderNumber: this.tenderId
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));
      this._loaderService.hideLoader();

      if (response && (response.IsSuccess || response.isSuccess || response.data)) {
        this.toasterService.showSuccess('Tender approved successfully', 'Success');
        // Refresh to get updated workflow state - user info will come from backend
        await this.fetchTenderDetails();
        this.initializeWorkflowData(); // Reinitialize workflow timeline
      } else {
        const errorMsg = response?.ErrorMessageEn || response?.errorMessageEn || response?.ErrorMessage || 'Failed to approve tender';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMsg = error?.error?.ErrorMessageEn || error?.message || 'An error occurred while approving the tender';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Tender Details] Error approving tender:', error);
    }
  }

  async publishTender(): Promise<void> {
    try {
      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.PublishTender}`;
      const payload = {
        TenderNumber: this.tenderId
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));
      this._loaderService.hideLoader();

      if (response && (response.IsSuccess || response.isSuccess || response.data)) {
        this.toasterService.showSuccess('Tender published successfully', 'Success');
        // Refresh to get updated workflow state - user info will come from backend
        await this.fetchTenderDetails();
        this.initializeWorkflowData(); // Reinitialize workflow timeline
      } else {
        const errorMsg = response?.ErrorMessageEn || response?.errorMessageEn || response?.ErrorMessage || 'Failed to publish tender';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMsg = error?.error?.ErrorMessageEn || error?.message || 'An error occurred while publishing the tender';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[Tender Details] Error publishing tender:', error);
    }
  }

  async closeBidding(): Promise<void> {
    if (!this.canCloseBidding || !this.tenderId) {
      return;
    }

    if (!window.confirm('Close bidding for this item? Vendors will no longer be able to submit bids.')) {
      return;
    }

    try {
      this._loaderService.showLoader();
      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.CloseTenderForBidding}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, { TenderNumber: this.tenderId }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bidding closed successfully', 'Success');
        await this.fetchTenderDetails();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to close bidding', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'Failed to close bidding', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async reopenBidding(): Promise<void> {
    if (!this.canReopenBidding || !this.tenderId) {
      return;
    }

    if (!window.confirm('Re-open bidding for this item? Vendors will be able to submit bids until the applicable closure date.')) {
      return;
    }

    try {
      this._loaderService.showLoader();
      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.ReopenTenderForBidding}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, { TenderNumber: this.tenderId }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Bidding re-opened successfully', 'Success');
        await this.fetchTenderDetails();
      } else {
        this.toasterService.showError(response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to re-open bidding', 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.error?.errorMessageEn || error?.error?.ErrorMessageEn || 'Failed to re-open bidding', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async requestTenderChange(comments: string = this.workflowComments): Promise<void> {
    try {
      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.RequestTenderChange}`;
      const payload = {
        TenderNumber: this.tenderId,
        comments: comments?.trim() || null,
        Comments: comments?.trim() || null
      };

      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));
      this._loaderService.hideLoader();

      if (response && (response.IsSuccess || response.isSuccess || response.data)) {
        this.toasterService.showSuccess('Change request sent successfully', 'Success');
        await this.fetchTenderDetails();
        this.initializeWorkflowData();
      } else {
        const errorMsg = response?.ErrorMessageEn || response?.errorMessageEn || response?.ErrorMessage || 'Failed to request changes';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMsg = error?.error?.ErrorMessageEn || error?.message || 'An error occurred while requesting changes';
      this.toasterService.showError(errorMsg, 'Error');
      console.error('[practice Details] Error requesting changes:', error);
    }
  }
  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.PRACTICE}`]);
  }

  exportData(): void {
    // TODO: Implement export functionality
  }

  determineWorkflowAction(): void {
    this.workflowAction = (this.getActiveWorkflowStepLabel() || 'Approve') as any;
    this.cdr.detectChanges();
  }
  canPerformWorkflowAction(): boolean {
    return this.currentUserMatchesWorkflowRole(this.getActiveWorkflowResponsibleRole());
  }
  canRequestChangeWorkflowAction(): boolean {
    const activeStep = this.getActiveWorkflowStep();
    if (Number(activeStep?.stageNumber ?? -1) === 1) return false;

    return this.currentUserMatchesWorkflowRole(this.getActiveWorkflowResponsibleRole());
  }


  private canApproveCurrentWorkflowStep(): boolean {
    return this.currentUserMatchesWorkflowRole(this.getActiveWorkflowResponsibleRole());
  }

  private getActiveWorkflowStepLabel(): string {
    const activeStep = this.getActiveWorkflowStep();
    return activeStep?.stepName || '';
  }

  private getActiveWorkflowStepName(): string {
    const activeStep = this.getActiveWorkflowStep();
    return activeStep ? this.normalizeWorkflowStepName(activeStep.stepName || '') : '';
  }

  private getActiveWorkflowResponsibleRole(): string {
    const activeStep = this.getActiveWorkflowStep();
    return activeStep?.responsibleRole || activeStep?.assignedTo || '';
  }

  private getActiveWorkflowStep(): any {
    return (this.timelineSteps || []).find((step: any) => step.actionRequired || step.status === 'active');
  }

  private canSubmitWorkflow(): boolean {
    return this.currentUserMatchesWorkflowRole(this.getActiveWorkflowResponsibleRole());
  }

  private canPublishWorkflow(): boolean {
    return this.currentUserMatchesWorkflowRole(this.getActiveWorkflowResponsibleRole());
  }

  private canApproveWorkflow(): boolean {
    return this.currentUserMatchesWorkflowRole(this.getActiveWorkflowResponsibleRole());
  }
  private currentUserMatchesWorkflowRole(configuredRoles: string): boolean {
    const normalizedCurrentRole = this.normalizeWorkflowRole(this.getCurrentUserRoleDisplayName());
    if (!normalizedCurrentRole || !configuredRoles) return false;

    return configuredRoles
      .split(',')
      .map(role => this.normalizeWorkflowRole(role))
      .some(role => role === normalizedCurrentRole);
  }

  private getCurrentUserRoleDisplayName(): string {
    return this.getWorkflowRoleDisplayName(this.userService.user?.Role);
  }

  private getWorkflowRoleDisplayName(role: any): string {
    const roleValue = typeof role === 'string' && !isNaN(Number(role)) ? Number(role) : role;

    switch (roleValue) {
      case 1: return 'TCM';
      case 2: return 'Vice Chairman';
      case 3: return 'Chairman';
      case 4: return 'Secretary';
      case 5: return 'Observer';
      case 6: return 'Evaluator';
      case 7: return 'Procurement Officer';
      default: return (role || '').toString();
    }
  }

  private normalizeWorkflowRole(role: any): string {
    return this.getWorkflowRoleDisplayName(role)
      .toString()
      .trim()
      .toUpperCase()
      .replace(/TENDERCOMMITTEE/g, '')
      .replace(/PROCURMENT/g, 'PROCUREMENT')
      .replace(/[^A-Z0-9]/g, '');
  }

  private isProcurementOfficer(): boolean {
    const userRole = this.userService.user?.Role;
    const roleValue = typeof userRole === 'string' && !isNaN(Number(userRole)) ? Number(userRole) : userRole;
    const normalizedRole = (userRole || '').toString().trim().toUpperCase().replace(/\s+/g, '');

    return normalizedRole === 'PROCUREMENTOFFICER' ||
      normalizedRole === 'PROCURMENTOFFICER' ||
      roleValue === 7;
  }

  private isSecretary(): boolean {
    const userRole = this.userService.user?.Role;
    const roleValue = typeof userRole === 'string' && !isNaN(Number(userRole)) ? Number(userRole) : userRole;
    const normalizedRole = (userRole || '').toString().trim().toUpperCase().replace(/\s+/g, '');

    return normalizedRole === 'SECRETARY' ||
      normalizedRole === 'TENDERCOMMITTEESECRETARY' ||
      roleValue === 4;
  }

  initializeWorkflowData(): void {
    const configuredStages = this.getConfiguredWorkflowStages();
    const stages = configuredStages.length > 0 ? configuredStages : this.getDefaultWorkflowStages();
    const hasApprovalStep = stages.some((stage: any) => this.normalizeWorkflowStepName(stage.stepName) === 'approval');
    const hasCreateStep = stages.some((stage: any) => this.normalizeWorkflowStepName(stage.stepName) === 'create');
    const timelineSource = hasCreateStep
      ? stages
      : [{ stageNumber: 0, stepName: 'Create Tender', responsibleRole: '' }, ...stages];

    this.timelineSteps = timelineSource
      .map((stage: any) => this.buildWorkflowTimelineStep(stage, hasApprovalStep))
      .sort((a: any, b: any) => (a.stageNumber ?? 0) - (b.stageNumber ?? 0));

    this.determineWorkflowAction();
  }

  private getConfiguredWorkflowStages(): any[] {
    return (this.workflowStages || [])
      .map((stage: any) => ({
        stageNumber: Number(stage.stageNumber ?? stage.StageNumber ?? 0),
        stepName: stage.stepName || stage.StepName || '',
        responsibleRole: stage.responsibleRole || stage.ResponsibleRole || stage.userRole || stage.UserRole || '',
        description: stage.description || stage.Description || ''
      }))
      .filter((stage: any) => stage.stepName)
      .sort((a: any, b: any) => (a.stageNumber ?? 0) - (b.stageNumber ?? 0));
  }

  private getDefaultWorkflowStages(): any[] {
    return [
      { stageNumber: 1, stepName: 'Submission', responsibleRole: '' },
      { stageNumber: 2, stepName: 'Publishing', responsibleRole: '' }
    ];
  }

  private buildWorkflowTimelineStep(stage: any, hasApprovalStep: boolean): any {
    const normalizedStep = this.normalizeWorkflowStepName(stage.stepName);
    const responsibleRole = stage.responsibleRole || '';
    const currentWorkflowStatus = (this.workflowStatus || '').toString().toLowerCase().replace(/\s+/g, '');
    const isNotSubmittedWorkflow = !currentWorkflowStatus || currentWorkflowStatus === 'notsubmitted' || currentWorkflowStatus === '0';
    const isPendingApprovalWorkflow = currentWorkflowStatus === 'pendingapproval';
    const isPendingPublicationWorkflow = currentWorkflowStatus === 'pendingpublication' || currentWorkflowStatus === 'approved';
    const isPublishedWorkflow = currentWorkflowStatus === 'published';
    const baseStep = {
      stageNumber: Number(stage.stageNumber ?? 0),
      stepName: stage.stepName || 'Workflow Step',
      assignedTo: responsibleRole || '',
      responsibleRole: responsibleRole || '',
      icon: 'check'
    };
    const isStageOne = Number(baseStep.stageNumber) === 1;

    if (normalizedStep === 'create') {
      return {
        ...baseStep,
        title: this.createdBy || 'Tender Created',
        description: this.createdBy ? `Created by ${this.createdBy}` : 'Tender was created',
        date: this.createdByDate || '',
        status: this.createdBy ? 'completed' : 'future'
      };
    }
    if (isStageOne) {
      const actionRequired = isNotSubmittedWorkflow;
      return {
        ...baseStep,
        title: actionRequired ? 'Awaiting Submission' : (this.submittedBy || 'Awaiting Submission'),
        description: actionRequired ? (responsibleRole ? ('Pending on ' + responsibleRole) : 'Tender submitted for publishing') : (this.submittedBy ? ('Submitted by ' + this.submittedBy) : (responsibleRole ? ('Assigned to ' + responsibleRole) : 'Tender submitted for publishing')),
        date: actionRequired ? '' : (this.submittedByDate || ''),
        status: actionRequired ? 'active' : (this.submittedBy || this.approvedBy || this.publishedBy || isPendingApprovalWorkflow || isPendingPublicationWorkflow || isPublishedWorkflow ? 'completed' : 'future'),
        actionRequired: actionRequired ? (stage.description || '') : null,
        pendingOn: actionRequired ? (responsibleRole || null) : null
      };
    }

    if (normalizedStep === 'submission') {
      const actionRequired = isNotSubmittedWorkflow;
      return {
        ...baseStep,
        title: actionRequired ? 'Awaiting Submission' : (this.submittedBy || 'Awaiting Submission'),
        description: actionRequired ? (responsibleRole ? ('Pending on ' + responsibleRole) : 'Tender submitted for publishing') : (this.submittedBy ? ('Submitted by ' + this.submittedBy) : (responsibleRole ? ('Assigned to ' + responsibleRole) : 'Tender submitted for publishing')),
        date: actionRequired ? '' : (this.submittedByDate || ''),
        status: actionRequired ? 'active' : (this.submittedBy || this.approvedBy || this.publishedBy || isPublishedWorkflow ? 'completed' : 'future'),
        actionRequired: actionRequired ? (stage.description || '') : null,
        pendingOn: actionRequired ? (responsibleRole || null) : null
      };
    }

    if (normalizedStep === 'approval') {
      const actionRequired = hasApprovalStep && isPendingApprovalWorkflow && !this.publishedBy;
      return {
        ...baseStep,
        title: this.approvedBy || 'Awaiting Approval',
        description: this.approvedBy ? ('Approved by ' + this.approvedBy) : (responsibleRole ? ('Pending on ' + responsibleRole) : 'Tender approval'),
        date: this.approvedByDate || '',
        status: this.approvedBy || this.publishedBy || isPendingPublicationWorkflow || isPublishedWorkflow ? 'completed' : (actionRequired ? 'active' : 'future'),
        actionRequired: actionRequired ? (stage.description || '') : null,
        pendingOn: actionRequired ? (responsibleRole || null) : null
      };
    }

    if (normalizedStep === 'publishing') {
      const actionRequired = isPendingPublicationWorkflow && !this.publishedBy;
      return {
        ...baseStep,
        title: this.publishedBy || 'Awaiting Publication',
        description: this.publishedBy ? ('Published by ' + this.publishedBy) : (responsibleRole ? ('Pending on ' + responsibleRole) : 'Tender published'),
        date: this.publishedByDate || '',
        status: this.publishedBy || isPublishedWorkflow ? 'completed' : (actionRequired ? 'active' : 'future'),
        actionRequired: actionRequired ? (stage.description || '') : null,
        pendingOn: actionRequired ? (responsibleRole || null) : null
      };
    }

    return {
      ...baseStep,
      title: stage.description || this.getWorkflowStepDisplayName(stage.stepName),
      description: responsibleRole ? `Assigned to ${responsibleRole}` : (stage.description || ''),
      date: '',
      status: 'future'
    };
  }

  private getResponsibleRoleForWorkflowStep(normalizedStepName: string): string {
    return '';
  }

  private normalizeWorkflowStepName(stepName: string): string {
    const normalized = (stepName || '').toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '');

    if (normalized === 'createtender' || normalized === 'create') return 'create';
    if (normalized === 'submit' || normalized === 'submission') return 'submission';
    if (normalized.startsWith('approve') || normalized === 'approval') return 'approval';
    if (normalized.startsWith('publish') || normalized === 'publishing' || normalized === 'publication') return 'publishing';

    return normalized;
  }

  private getWorkflowStepDisplayName(stepName: string): string {
    const normalizedStep = this.normalizeWorkflowStepName(stepName);

    switch (normalizedStep) {
      case 'create': return 'Create Tender';
      case 'submission': return 'Submission';
      case 'approval': return 'Approval';
      case 'publishing': return 'Publishing';
      default: return stepName || 'Workflow Step';
    }
  }
  formatWorkflowStatus(status: any): string {
    if (!status) return 'Not Submitted';

    // Handle enum values (numbers)
    if (typeof status === 'number') {
      switch(status) {
        case 0: return 'Not Submitted';
        case 1: return 'Pending Publication';
        case 2: return 'Approved';
        case 3: return 'Rejected';
        case 4: return 'Published';
        default: return 'Unknown';
      }
    }

    // Handle string values
    const statusStr = String(status).trim();
    if (!statusStr) return 'Not Submitted';

    // Convert common formats to display format
    switch(statusStr.toLowerCase()) {
      case 'notsubmitted':
      case 'not_submitted':
        return 'Not Submitted';
      case 'pendingapproval':
      case 'pending_approval':
        return 'Pending Publication';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'published':
        return 'Published';
      default:
        // Return formatted version (e.g., "PendingApproval" -> "Pending Approval")
        return statusStr.replace(/([A-Z])/g, ' $1').trim();
    }
  }

  getStatusColor(status: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    if (!status) return 'gray';

    const statusLower = status.toLowerCase().trim();

    switch(statusLower) {
      case 'completed':
      case 'approved':
      case 'published':
        return 'green';
      case 'in progress':
      case 'under evaluation':
      case 'active':
        return 'blue';
      case 'pending':
      case 'draft':
      case 'pending approval':
        return 'orange';
      case 'cancelled':
      case 'rejected':
        return 'red';
      case 'submitted':
      case 'not submitted':
        return 'purple';
      default:
        return 'gray';
    }
  }

  getWorkflowStatusColor(status: string): string {
    if (!status) return '#6c757d'; // gray

    const statusLower = status.toLowerCase().trim();

    switch(statusLower) {
      case 'approved':
        return '#28a745'; // green
      case 'published':
        return '#17a2b8'; // cyan/blue
      case 'pending publication':
      case 'pendingpublication':
      case 'pending approval':
      case 'pendingapproval':
        return '#E46A11'; // orange
      case 'rejected':
        return '#dc3545'; // red
      case 'not submitted':
      case 'notsubmitted':
        return '#652D86'; // purple
      default:
        return '#6c757d'; // gray
    }
  }

  onTabChange(tab: string): void {
    console.log('[Tender Details] Tab changed to:', tab, 'Current tenderId:', this.tenderId);
    this.activeTab = tab;
    if (tab === 'Documents') {
      console.log('[Tender Details] Documents tab clicked, loading documents...');
      // Use setTimeout to ensure change detection runs and tab is updated first
      setTimeout(() => {
        this.loadTenderDocuments();
      }, 0);
    }
  }

  async loadTenderDocuments(): Promise<void> {
    console.log('========================================');
    console.log('[Tender Details] loadTenderDocuments CALLED');
    console.log('[Tender Details] tenderId:', this.tenderId);
    console.log('[Tender Details] isLoadingDocuments:', this.isLoadingDocuments);
    console.log('========================================');

    if (!this.tenderId) {
      console.error('[Tender Details] ERROR: No tender ID available to load documents');
      return;
    }

    if (this.isLoadingDocuments) {
      console.log('[Tender Details] Already loading documents, skipping...');
      return; // Prevent multiple simultaneous requests
    }

    this.isLoadingDocuments = true;
    this._loaderService.showLoader();

    try {
      // Call the dedicated API to get tender documents
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/Tenders/${AppConstants.ACTIONS.GetTenderDocuments}`;
      console.log('========================================');
      console.log('[Tender Details] MAKING API CALL FOR DOCUMENTS');
      console.log('[Tender Details] URL:', url);
      console.log('[Tender Details] Query Params:', { tenderNumber: this.tenderId });
      console.log('[Tender Details] Full URL will be:', `${url}?tenderNumber=${this.tenderId}`);
      console.log('========================================');

      const apiCall$ = this.apiService.getByUrl(url, { tenderNumber: this.tenderId });
      console.log('[Tender Details] API Observable created, subscribing...');

      const response: any = await lastValueFrom(apiCall$);

      console.log('[Tender Details] API Response:', response);
      console.log('[Tender Details] Response isSuccess:', response?.isSuccess);
      console.log('[Tender Details] Response data:', response?.data);

      if (response?.isSuccess && response?.data) {
        // The response.data is directly the list of documents
        const documents = response.data || [];
        console.log('[Tender Details] Documents found:', documents.length, documents);

        // Normalize document properties (handle both camelCase and PascalCase from JSON)
        // fileContent now contains blob storage URL instead of base64
        const normalizedDocuments = documents.map((doc: any) => ({
          attachmentId: doc.attachmentId || doc.AttachmentId || '',
          name: doc.name || doc.Name || '',
          additionalDetails: doc.additionalDetails || doc.AdditionalDetails || '',
          fileContent: doc.fileContent || doc.FileContent || '', // This is now a URL, not base64
          mimeType: doc.mimeType || doc.MimeType || '',
          attachmentFor: doc.attachmentFor || doc.AttachmentFor || 'Other'
        }));

        // Separate documents by AttachmentFor
        this.tenderDocuments = normalizedDocuments.filter((doc: any) => doc.attachmentFor === 'Tender' || doc.attachmentFor === 'Tender Documents');
        this.bidDocuments = normalizedDocuments.filter((doc: any) => doc.attachmentFor === 'Bid' || doc.attachmentFor === 'Bidding');

        // Group documents for display (similar to vendor portal)
        this.groupedAttachments = Object.entries(
          normalizedDocuments.reduce((group: Record<string, any[]>, item: any) => {
            const key = item.attachmentFor || 'Other';
            if (!group[key]) {
              group[key] = [];
            }
            group[key].push(item);
            return group;
          }, {})
        ).map(([key, value]) => ({
          attachmentFor: key,
          items: value as any[]
        }));

        // Trigger change detection to update the view
        this.cdr.detectChanges();
      } else {
        // No documents found, still trigger change detection
        this.tenderDocuments = [];
        this.bidDocuments = [];
        this.groupedAttachments = [];
        this.cdr.detectChanges();
      }
    } catch (error: any) {
      console.error('[Tender Details] Error loading documents:', error);
      this.toasterService.showError(
        this.tenderHelper.getErrorMessage(error) || 'Failed to load documents',
        'Error'
      );
      // Reset documents on error and trigger change detection
      this.tenderDocuments = [];
      this.bidDocuments = [];
      this.groupedAttachments = [];
      this.cdr.detectChanges();
    } finally {
      this.isLoadingDocuments = false;
      this._loaderService.hideLoader();
      // Ensure change detection runs after loading completes
      this.cdr.detectChanges();
    }
  }

  addTenderDocument(): void {
    this.tenderDocuments.push({
      attachmentId: '',
      name: '',
      additionalDetails: '',
      fileContent: '',
      mimeType: '',
      attachmentFor: 'Tender',
      isNew: true
    });
    this.cdr.detectChanges();
  }

  removeTenderDocument(index: number): void {
    const document = this.tenderDocuments[index];
    if (!document) return;

    const attachmentId = this.getTrimmedValue(document.attachmentId || document.AttachmentId);
    if (!document.isNew && attachmentId && !this.deletedTenderDocumentIds.includes(attachmentId)) {
      this.deletedTenderDocumentIds.push(attachmentId);
    }

    this.tenderDocuments.splice(index, 1);
    this.cdr.detectChanges();
  }

  onTenderDocumentFileSelected(event: any, index: number): void {
    const file = event.target.files?.[0];
    if (!file) return;

    const document = this.tenderDocuments[index];
    if (!document) return;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const base64String = e.target.result || '';
      document.fileContent = base64String.includes(',') ? base64String.split(',')[1] : base64String;
      document.mimeType = file.type || this.getMimeTypeFromFileName(file.name);
      document.name = this.getTrimmedValue(document.name) || file.name;
      document.isNew = true;
      document.attachmentId = '';
      this.cdr.detectChanges();
    };
    reader.onerror = () => {
      this.toasterService.showError('Error reading file. Please try again.', 'File Error');
    };
    reader.readAsDataURL(file);
  }
  async downloadFile(fileContent: string, fileName: string, mimeType?: string): Promise<void> {
    try {
      // Check if fileContent is a URL (starts with http:// or https://)
      if (fileContent && (fileContent.startsWith('http://') || fileContent.startsWith('https://'))) {
        // It's a blob storage URL - fetch and download (same as meetings implementation)
        const response = await fetch(fileContent, {
          method: 'GET'
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const blob = await response.blob();

        // Infer MIME type from response or file name if not provided
        if (!mimeType) {
          mimeType = blob.type || (fileName.includes('.')
            ? this.getMimeTypeFromExtension(fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase())
            : 'application/octet-stream') || 'application/octet-stream';
        }

        // Create a File object with the filename
        const file = new File([blob], fileName, { type: mimeType });
        const fileURL = URL.createObjectURL(file);

        const link = document.createElement('a');
        link.href = fileURL;
        link.download = fileName;
        link.click();

        // Clean up the blob URL after a short delay
        setTimeout(() => {
          URL.revokeObjectURL(fileURL);
        }, 100);
        return;
      }

      // Otherwise, treat as base64 (for backward compatibility)
      let base64Data = fileContent;

      // If MIME type is not provided, try to extract it from the Base64 string
      if (!mimeType) {
        const base64HeaderMatch = fileContent.match(/^data:(.+);base64,(.+)$/);
        if (base64HeaderMatch && base64HeaderMatch.length === 3) {
          mimeType = base64HeaderMatch[1];
          base64Data = base64HeaderMatch[2];
        } else if (fileContent.includes(',')) {
          base64Data = fileContent.split(',')[1];
        }
      }

      // Infer MIME type from file name if still not provided
      if (!mimeType && fileName.includes('.')) {
        const extension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
        mimeType = this.getMimeTypeFromExtension(extension) || 'application/octet-stream';
      }

      const byteCharacters = atob(base64Data);
      const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      const link = document.createElement('a');
      const fileURL = URL.createObjectURL(blob);

      link.href = fileURL;
      link.download = fileName;
      link.click();

      URL.revokeObjectURL(fileURL);
    } catch (e) {
      console.error('Error processing file for download:', e);
      this.toasterService.showError('Error downloading file', 'Error');
    }
  }

  async viewFile(fileContent: string, fileName: string, mimeType?: string): Promise<void> {
    try {
      // Check if fileContent is a URL (starts with http:// or https://)
      if (fileContent && (fileContent.startsWith('http://') || fileContent.startsWith('https://'))) {
        // It's a blob storage URL - fetch and view (same as meetings implementation)
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
                         /iPad|iPhone|iPod/.test(navigator.userAgent);

        // For Safari/iPad: Open the original URL directly in a new tab
        if (isSafari) {
          window.open(fileContent, '_blank', 'noopener,noreferrer');
          return;
        }

        // For Chrome/other browsers: Fetch as blob to prevent downloads
        // Fetch the file as a blob to create an object URL for preview
        // This prevents the browser from downloading the file due to Content-Disposition headers
        const response = await fetch(fileContent, {
          method: 'GET'
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const blob = await response.blob();

        // Infer MIME type from response or file name if not provided
        if (!mimeType) {
          mimeType = blob.type || (fileName.includes('.')
            ? this.getMimeTypeFromExtension(fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase())
            : 'application/octet-stream') || 'application/octet-stream';
        }

        // Check if the file type can be viewed in browser
        const viewableTypes = ['image/', 'application/pdf', 'text/'];
        const canView = viewableTypes.some(type => mimeType?.startsWith(type));

        if (!canView) {
          this.toasterService.showWarning('This file type cannot be viewed in the browser. Please download it instead.', 'Cannot View');
          return;
        }

        const file = new File([blob], fileName, { type: mimeType });
        const fileURL = URL.createObjectURL(file);

        // Open in new tab with file loaded
        window.open(fileURL, '_blank', 'noopener,noreferrer');

        // Don't revoke the blob URL - let browser handle cleanup when tab closes
        return;
      }

      // Otherwise, treat as base64 (for backward compatibility)
      let base64Data = fileContent;

      // If MIME type is not provided, try to extract it from the Base64 string
      if (!mimeType) {
        const base64HeaderMatch = fileContent.match(/^data:(.+);base64,(.+)$/);
        if (base64HeaderMatch && base64HeaderMatch.length === 3) {
          mimeType = base64HeaderMatch[1];
          base64Data = base64HeaderMatch[2];
        } else if (fileContent.includes(',')) {
          base64Data = fileContent.split(',')[1];
        }
      }

      // Infer MIME type from file name if still not provided
      if (!mimeType && fileName.includes('.')) {
        const extension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
        mimeType = this.getMimeTypeFromExtension(extension) || 'application/octet-stream';
      }

      // Check if the file type can be viewed in browser
      const viewableTypes = ['image/', 'application/pdf', 'text/'];
      const canView = viewableTypes.some(type => mimeType?.startsWith(type));

      if (!canView) {
        this.toasterService.showWarning('This file type cannot be viewed in the browser. Please download it instead.', 'Cannot View');
        return;
      }

      const byteCharacters = atob(base64Data);
      const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, '_blank');

      // Clean up the URL after a delay to allow the browser to load it
      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 100);
    } catch (e) {
      console.error('Error processing file for viewing:', e);
      this.toasterService.showError('Error viewing file. Please try downloading instead.', 'Preview Error');
    }
  }

  private getMimeTypeFromExtension(extension: string): string | undefined {
    const mimeTypes: { [key: string]: string } = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };
    return mimeTypes[extension];
  }
}
